import { StrictMode, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import {
  buildPortableArchiveTextFiles,
  type CloudBoardState,
  type CloudCaptureStatus,
  type EffectiveCloudPlan,
  type CloudRestoreHistory,
  type CloudRestoreReport,
  type CloudSnapshotHistory,
  type CloudSnapshotWarnings,
  type CloudSettingsUpdateRequest,
  type JobControlAction,
  type RestorePreview,
  type RestorePreparationResponse,
  createLocalArchiveFilename,
  createPortableZip,
  createSnapshotManifest,
  normalizeTrelloCapture,
} from "@board-preserve/contracts";

import {
  confirmCloudRestore,
  CloudApiError,
  connectCloudPreserve,
  controlCloudJob,
  deleteCloudSnapshot,
  disconnectCloudPreserve,
  downloadCloudSnapshot,
  startNativeCloudSnapshotDownload,
  readCloudHistory,
  readCloudSnapshotWarnings,
  readCloudCaptureStatus,
  readCloudRestoreHistory,
  readCloudRestoreReport,
  readCloudSettings,
  previewCloudRestore,
  readActiveCloudCaptures,
  readCloudApiUrl,
  startCloudCapture,
  updateCloudSettings,
} from "./cloud-connection";
import {
  applyHistoryScheduleState,
  startCloudHistoryRefresh,
} from "./cloud-history-refresh";
import { InPlaceRestorePanel } from "./in-place-restore-panel";
import { PlanAndUsagePanel, PlanComparisonPanel } from "./plan-and-usage-panel";
import { ArchiveIcon } from "./power-up-icons";
import { RestoreFromZipGate } from "./restore-from-zip-gate";
import { RecoveryPointPicker } from "./recovery-point-picker";
import { SnapshotComparisonPanel } from "./snapshot-comparison-panel";
import { ActiveWork, WorkspaceOverview } from "./workspace-overview";
import { BackupSchedulePanel } from "./backup-schedule-panel";
import { BackupHistoryPanel } from "./backup-history-panel";
import {
  ManagedBackupPanel,
  type ManagedCaptureTiming,
} from "./managed-backup-panel";
import {
  TaskScreenHeader,
  WorkspaceBootstrap,
  WorkspaceContextHeader,
} from "./task-hub-shell";
import { cloudTrialAvailabilityLabel } from "./cloud-trial-presentation";
import {
  captureLocalAttachments,
  MAX_LOCAL_ARCHIVE_BYTES,
  type LocalAttachmentProgress,
} from "./local-attachments";
import {
  downloadBlob,
  downloadResponse,
  MAX_MEMORY_DOWNLOAD_BYTES,
  prepareDownloadTarget,
} from "./local-download";
import {
  progressTimeRemainingPresentation,
  restoreProgressPresentation,
} from "./job-progress-presentation";
import {
  isLocalExportPreflightComplete,
  localExportPreflight,
  type LocalExportPreflightId,
  type PreserveModeId,
} from "./preserve-modes";
import { resolveRestoreSnapshotSelection } from "./restore-snapshot-selection";
import {
  nextRestoreReportJobId,
  newestRestoreReports,
  restoreReportDisclosureLabel,
  visibleRestoreReports as selectVisibleRestoreReports,
} from "./restore-report-presentation";
import {
  detectedScheduleTimeZone,
  scheduleTimeZoneOptions,
} from "./schedule-timezones";
import { SelectiveRestorePanel } from "./selective-restore-panel";
import { createTrelloAuthorizationSetup } from "./trello-authorization";
import { readTrelloBoard, type TrelloReadProgress } from "./trello-reader";
import { UploadedRestorePanel } from "./uploaded-restore-panel";
import { rebindUploadedRestoreJob } from "./uploaded-restore-client";
import {
  createCloudArchiveFilename,
  timezoneOffsetMinutesForCapture,
} from "./cloud-archive-filename";
import "./styles.css";
import "./redesign.css";

type AuthorizationState =
  | "authorized"
  | "authorizing"
  | "available"
  | "checking"
  | "client_unavailable"
  | "connecting"
  | "failed"
  | "unconfigured";

interface LocalBoardSummary {
  activities: number;
  archiveBytes: number;
  archiveFilename: string;
  archiveFiles: number;
  attachments: number;
  cards: number;
  checklists: number;
  customFields: number;
  linkOnlyAttachments: number;
  lists: number;
  skippedAttachments: number;
  warnings: number;
}

type LocalReadState =
  | { status: "cancelled" }
  | { status: "complete"; summary: LocalBoardSummary }
  | { message: string; status: "failed" }
  | { status: "idle" }
  | { progress: LocalAttachmentProgress; status: "attachments" }
  | { status: "building" }
  | { status: "preparing" }
  | { progress: TrelloReadProgress | null; status: "reading" };

type CloudSaveState = "idle" | "saving" | "saved" | "failed";
type CloudCaptureState =
  | "accepted"
  | "cancelled"
  | "complete"
  | "failed"
  | "idle"
  | "paused"
  | "processing"
  | "submitting";
type CloudHistoryState = "failed" | "idle" | "loading" | "ready";
type CloudRestorePreviewState = "failed" | "idle" | "loading" | "ready";
type CloudRestoreConfirmState = "failed" | "idle" | "submitting" | "submitted";
type CloudRestoreReportState = "failed" | "idle" | "loading" | "ready";
type CloudDownloadState = "complete" | "downloading" | "failed" | "idle";
type CloudDisconnectState = "disconnecting" | "failed" | "idle" | "success";
type CloudTab =
  "backup" | "history" | "plan" | "recover" | "schedule" | "settings";
type CloudAuthorizationDestination = CloudTab | "overview";
type JobsFilter = "all" | "backups" | "recoveries";
type RecoveryIntent =
  "compare" | "in_place" | "new_board" | "reports" | "selective" | "zip" | null;
type PlanView = "compare" | "usage";
type WorkspaceBootstrapState =
  "checking" | "connected" | "disconnected" | "unavailable";

const progressLabels: Readonly<Record<TrelloReadProgress["stage"], string>> = {
  actions: "Reading card activity",
  board: "Reading board settings",
  cards: "Reading cards",
  checklists: "Reading checklists",
  custom_fields: "Reading custom fields",
  labels: "Reading labels",
  lists: "Reading lists",
  members: "Reading members",
  memberships: "Reading board roles",
};

function formatBytes(bytes: number): string {
  if (bytes < 1_000) {
    return `${bytes} B`;
  }

  if (bytes < 1_000_000) {
    return `${(bytes / 1_000).toFixed(1)} KB`;
  }

  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function cloudPlanLabel(
  plan: EffectiveCloudPlan,
): "Pro" | "Pro Extended" | "Trial" {
  return plan === "pro_extended"
    ? "Pro Extended"
    : plan === "pro"
      ? "Pro"
      : "Trial";
}

function formatRestoreStatus(status: CloudRestoreReport["status"]): string {
  return {
    canceled: "Cancelled",
    complete: "Complete",
    complete_with_warnings: "Complete with warnings",
    failed: "Failed",
    paused: "Paused",
    pending: "Waiting",
    running: "Restoring",
  }[status];
}

function formatRestoreResultCode(resultCode: string): string {
  return (
    {
      card_cover_attachment_unavailable:
        "Cover skipped: its source attachment was not available",
      card_cover_format_unsupported:
        "Cover skipped: Trello returned an unsupported cover format",
      card_cover_provider_unavailable:
        "Cover skipped: provider photo identifiers cannot be reused on another board",
    }[resultCode] ?? resultCode.replaceAll("_", " ")
  );
}

function progressPercentage(completed: number, total: number | null): number {
  if (total === null || total <= 0) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.round((completed / total) * 100)));
}

function App() {
  const lifecycleEntry = new URLSearchParams(window.location.search).get(
    "entry",
  );
  const isLifecycleEntry =
    lifecycleEntry === "authorization" || lifecycleEntry === "settings";
  const [selectedMode, setSelectedMode] = useState<PreserveModeId | null>(() =>
    isLifecycleEntry ? "cloud" : null,
  );
  const [acknowledgements, setAcknowledgements] = useState<
    Set<LocalExportPreflightId>
  >(new Set());
  const [trelloSetup] = useState(() =>
    createTrelloAuthorizationSetup(
      window.TrelloPowerUp,
      import.meta.env.VITE_TRELLO_API_KEY,
      window.location.href,
    ),
  );
  const [authorizationState, setAuthorizationState] =
    useState<AuthorizationState>(() =>
      trelloSetup.availability === "available"
        ? "checking"
        : trelloSetup.availability,
    );
  const [authorizedMode, setAuthorizedMode] = useState<PreserveModeId | null>(
    null,
  );
  const [authorizationError, setAuthorizationError] = useState<string | null>(
    null,
  );
  const [lifecycleMessage, setLifecycleMessage] = useState<string | null>(null);
  const [disconnectConfirmed, setDisconnectConfirmed] = useState(false);
  const [cloudDisconnectState, setCloudDisconnectState] =
    useState<CloudDisconnectState>("idle");
  const [localReadState, setLocalReadState] = useState<LocalReadState>({
    status: "idle",
  });
  const [cloudState, setCloudState] = useState<CloudBoardState | null>(null);
  const [workspaceBootstrapState, setWorkspaceBootstrapState] =
    useState<WorkspaceBootstrapState>(() =>
      trelloSetup.client ? "checking" : "unavailable",
    );
  const [cloudDraft, setCloudDraft] =
    useState<CloudSettingsUpdateRequest | null>(null);
  const [cloudSaveState, setCloudSaveState] = useState<CloudSaveState>("idle");
  const [cloudTab, setCloudTab] = useState<CloudTab>("backup");
  const [jobsFilter, setJobsFilter] = useState<JobsFilter>("all");
  const [recoveryIntent, setRecoveryIntent] = useState<RecoveryIntent>(null);
  const [planView, setPlanView] = useState<PlanView>("usage");
  const [cloudCaptureState, setCloudCaptureState] =
    useState<CloudCaptureState>("idle");
  const [cloudCaptureMessage, setCloudCaptureMessage] = useState<string | null>(
    null,
  );
  const [cloudCaptureProgress, setCloudCaptureProgress] = useState<{
    completed: number;
    total: number | null;
  } | null>(null);
  const [cloudCaptureProgressObservedAt, setCloudCaptureProgressObservedAt] =
    useState<number | null>(null);
  const cloudCaptureProgressSignature = useRef<string | null>(null);
  const [cloudCaptureTiming, setCloudCaptureTiming] =
    useState<ManagedCaptureTiming | null>(null);
  const [cloudLastFailedCapture, setCloudLastFailedCapture] =
    useState<CloudCaptureStatus | null>(null);
  const [cloudCaptureJobId, setCloudCaptureJobId] = useState<string | null>(
    null,
  );
  const [cloudCapturePurpose, setCloudCapturePurpose] =
    useState<CloudCaptureStatus["capturePurpose"]>(undefined);
  const [cloudJobControl, setCloudJobControl] = useState<{
    action: JobControlAction["action"];
    jobId: string;
  } | null>(null);
  const [cloudJobControlMessages, setCloudJobControlMessages] = useState<
    Record<string, string>
  >({});
  const [cloudJobPendingActions, setCloudJobPendingActions] = useState<
    Record<string, JobControlAction["action"] | undefined>
  >({});
  const cloudCaptureIdempotencyKey = useRef<string | null>(null);
  const reattachedCaptureMonitor = useRef<string | null>(null);
  const [cloudHistoryState, setCloudHistoryState] =
    useState<CloudHistoryState>("idle");
  const [cloudHistory, setCloudHistory] = useState<CloudSnapshotHistory | null>(
    null,
  );
  const [cloudHistoryError, setCloudHistoryError] = useState<string | null>(
    null,
  );
  const [cloudWarningReports, setCloudWarningReports] = useState<
    Record<
      string,
      {
        error: string | null;
        report: CloudSnapshotWarnings | null;
        state: "idle" | "loading" | "ready" | "failed";
      }
    >
  >({});
  const [cloudDownloadState, setCloudDownloadState] =
    useState<CloudDownloadState>("idle");
  const [cloudDownloadSnapshotId, setCloudDownloadSnapshotId] = useState<
    string | null
  >(null);
  const [cloudDownloadMessage, setCloudDownloadMessage] = useState<
    string | null
  >(null);
  const [cloudDeleteSnapshotId, setCloudDeleteSnapshotId] = useState<
    string | null
  >(null);
  const [cloudDeleteError, setCloudDeleteError] = useState<string | null>(null);
  const [selectedRestoreSnapshotId, setSelectedRestoreSnapshotId] = useState<
    string | null
  >(null);
  const [uploadedRestoreSnapshotId, setUploadedRestoreSnapshotId] = useState<
    string | null
  >(null);
  const [restoreReuploadJobId, setRestoreReuploadJobId] = useState<
    string | null
  >(null);
  const [cloudRestorePreviewState, setCloudRestorePreviewState] =
    useState<CloudRestorePreviewState>("idle");
  const [cloudRestorePreview, setCloudRestorePreview] =
    useState<RestorePreview | null>(null);
  const [cloudRestorePreviewError, setCloudRestorePreviewError] = useState<
    string | null
  >(null);
  const [cloudRestoreConfirmState, setCloudRestoreConfirmState] =
    useState<CloudRestoreConfirmState>("idle");
  const [cloudRestoreConfirmError, setCloudRestoreConfirmError] = useState<
    string | null
  >(null);
  const [cloudRestorePreparation, setCloudRestorePreparation] =
    useState<RestorePreparationResponse | null>(null);
  const [cloudRestoreAcknowledgements, setCloudRestoreAcknowledgements] =
    useState({
      duplicateRestore: false,
      fidelity: false,
      newBoard: false,
      noMutation: false,
    });
  const cloudRestoreIdempotencyKey = useRef<string | null>(null);
  const [cloudRestoreHistoryState, setCloudRestoreHistoryState] =
    useState<CloudHistoryState>("idle");
  const [cloudRestoreHistory, setCloudRestoreHistory] =
    useState<CloudRestoreHistory | null>(null);
  const [cloudRestoreHistoryError, setCloudRestoreHistoryError] = useState<
    string | null
  >(null);
  const [restoreReportsExpanded, setRestoreReportsExpanded] = useState(false);
  const [cloudRestoreReportState, setCloudRestoreReportState] =
    useState<CloudRestoreReportState>("idle");
  const [cloudRestoreReport, setCloudRestoreReport] =
    useState<CloudRestoreReport | null>(null);
  const [cloudRestoreReportJobId, setCloudRestoreReportJobId] = useState<
    string | null
  >(null);
  const [cloudRestoreReportError, setCloudRestoreReportError] = useState<
    string | null
  >(null);
  const [cloudSettingsError, setCloudSettingsError] = useState<string | null>(
    null,
  );
  const localReadAbortController = useRef<AbortController | null>(null);

  const preflightComplete = isLocalExportPreflightComplete(acknowledgements);
  const browserTimeZone = useMemo(detectedScheduleTimeZone, []);
  const scheduleTimeZones = useMemo(
    () =>
      scheduleTimeZoneOptions(
        cloudDraft?.schedule.frequency === "off"
          ? undefined
          : cloudDraft?.schedule.timeZone,
      ),
    [cloudDraft?.schedule],
  );
  const cloudSettingsDirty =
    cloudState !== null &&
    cloudDraft !== null &&
    JSON.stringify(cloudDraft) !==
      JSON.stringify({
        retentionDays: cloudState.settings.retentionDays,
        retentionTestMinutes: cloudState.settings.retentionTestMinutes ?? null,
        schedule: cloudState.settings.schedule,
        scheduleTestMinutes: cloudState.settings.scheduleTestMinutes ?? null,
      });
  const cloudTrialAvailability = cloudState
    ? cloudTrialAvailabilityLabel(cloudState.trial)
    : null;

  useEffect(() => {
    if (cloudCaptureProgress === null) return;
    const signature = `${cloudCaptureProgress.completed}:${cloudCaptureProgress.total ?? "unknown"}`;
    if (cloudCaptureProgressSignature.current === signature) return;
    cloudCaptureProgressSignature.current = signature;
    setCloudCaptureProgressObservedAt(Date.now());
  }, [cloudCaptureProgress]);

  async function hydrateWorkspaceConnection(
    isActive: () => boolean = () => true,
  ): Promise<void> {
    const client = trelloSetup.client;
    setWorkspaceBootstrapState("checking");
    if (!client) {
      setWorkspaceBootstrapState("unavailable");
      return;
    }

    const browserAuthorization = client.isAuthorized();
    const managedConnection = (async () => {
      const session = await client.createCloudContextSession("cloud-settings");
      return readCloudSettings(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
      );
    })();
    const [browserResult, managedResult] = await Promise.allSettled([
      browserAuthorization,
      managedConnection,
    ]);
    if (!isActive()) return;

    if (browserResult.status === "fulfilled") {
      setAuthorizationState(browserResult.value ? "authorized" : "available");
    } else {
      setAuthorizationState("failed");
    }

    if (managedResult.status === "fulfilled") {
      const connected = managedResult.value;
      setCloudState(connected);
      setCloudDraft({
        retentionDays: connected.settings.retentionDays,
        retentionTestMinutes: connected.settings.retentionTestMinutes ?? null,
        schedule: connected.settings.schedule,
        scheduleTestMinutes: connected.settings.scheduleTestMinutes ?? null,
      });
      setAuthorizedMode("cloud");
      setWorkspaceBootstrapState("connected");
      return;
    }

    const reason = managedResult.reason as unknown;
    if (reason instanceof CloudApiError && reason.status === 404) {
      setCloudState(null);
      setCloudDraft(null);
      setWorkspaceBootstrapState("disconnected");
      return;
    }
    setWorkspaceBootstrapState("unavailable");
  }

  useEffect(() => {
    let active = true;
    void hydrateWorkspaceConnection(() => active);
    return () => {
      active = false;
    };
  }, [trelloSetup]);

  useEffect(() => {
    if (!trelloSetup.client) {
      return;
    }

    const content = document.querySelector(".capture-card");

    if (!content) {
      return;
    }

    const resize = (): void => {
      void trelloSetup.client?.resizeToContent().catch(() => undefined);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(content);
    resize();

    return () => {
      observer.disconnect();
    };
  }, [trelloSetup]);

  useEffect(() => {
    if (!cloudState?.board.id) {
      return;
    }
    void reattachActiveCloudCapture();
    if (cloudHistoryState === "idle") {
      void loadCloudSnapshots(true);
    }
    if (cloudRestoreHistoryState === "idle") {
      void loadCloudRestores(true);
    }
  }, [cloudState?.board.id]);

  async function inspectLocalBoard(): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }

    const abortController = new AbortController();
    localReadAbortController.current = abortController;
    setLocalReadState({ progress: null, status: "reading" });

    try {
      const session = await trelloSetup.client.createLocalExportSession();
      const capture = await readTrelloBoard(session, {
        onProgress: (progress) => {
          setLocalReadState({ progress, status: "reading" });
        },
        signal: abortController.signal,
      });
      setLocalReadState({ status: "preparing" });
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
      if (abortController.signal.aborted) {
        throw new DOMException("Local export cancelled.", "AbortError");
      }

      const snapshot = normalizeTrelloCapture(capture, {
        capturedAt: new Date().toISOString(),
        snapshotId: crypto.randomUUID(),
      });
      const attachmentResults = await captureLocalAttachments(snapshot, {
        onProgress: (progress) => {
          setLocalReadState({ progress, status: "attachments" });
        },
        signal: abortController.signal,
      });
      const manifest = createSnapshotManifest(
        snapshot,
        attachmentResults.map(({ outcome }) => outcome),
      );
      setLocalReadState({ status: "building" });
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
      if (abortController.signal.aborted) {
        throw new DOMException("Local export cancelled.", "AbortError");
      }

      const textFiles = buildPortableArchiveTextFiles(snapshot, manifest);
      const archive = await createPortableZip(
        textFiles.map((file) => ({
          contents: file.contents,
          path: file.path,
        })),
        snapshot.capturedAt,
      );
      if (archive.byteLength > MAX_LOCAL_ARCHIVE_BYTES) {
        throw new RangeError(
          "The completed archive exceeds the 1 GB Local Export limit.",
        );
      }

      const archiveFilename = createLocalArchiveFilename(
        snapshot.board.name,
        snapshot.capturedAt,
      );
      downloadBlob(archive.blob, archiveFilename);
      const linkOnlyAttachments = manifest.attachments.filter(
        ({ status }) => status === "link_only",
      ).length;
      const skippedAttachments = manifest.attachments.filter(
        ({ status }) => status === "skipped",
      ).length;
      setLocalReadState({
        status: "complete",
        summary: {
          activities: snapshot.activities.length,
          archiveBytes: archive.byteLength,
          archiveFilename,
          archiveFiles: archive.entryCount,
          attachments: snapshot.attachments.length,
          cards: snapshot.cards.length,
          checklists: snapshot.checklists.length,
          customFields: snapshot.customFields.length,
          linkOnlyAttachments,
          lists: snapshot.lists.length,
          skippedAttachments,
          warnings: manifest.warnings.length,
        },
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setLocalReadState({ status: "cancelled" });
        return;
      }

      setLocalReadState({
        message:
          error instanceof Error
            ? error.message
            : "The board could not be read from Trello.",
        status: "failed",
      });
    } finally {
      if (localReadAbortController.current === abortController) {
        localReadAbortController.current = null;
      }
    }
  }

  function cancelLocalRead(): void {
    localReadAbortController.current?.abort();
  }

  async function authorizeTrello(
    mode: PreserveModeId,
    cloudDestination: CloudAuthorizationDestination = "backup",
  ): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }

    setAuthorizationState("authorizing");
    setAuthorizationError(null);

    try {
      await trelloSetup.client.authorize(mode);
      if (mode === "local") {
        setAuthorizedMode(mode);
        setAuthorizationState("authorized");
        await inspectLocalBoard();
      } else {
        setAuthorizationState("connecting");
        const session = await trelloSetup.client.createCloudConnectionSession();
        const connected = await connectCloudPreserve(
          readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
          session,
        );
        setCloudState(connected);
        setWorkspaceBootstrapState("connected");
        setCloudDraft({
          retentionDays: connected.settings.retentionDays,
          retentionTestMinutes: connected.settings.retentionTestMinutes ?? null,
          schedule: connected.settings.schedule,
          scheduleTestMinutes: connected.settings.scheduleTestMinutes ?? null,
        });
        setAuthorizedMode(mode);
        setAuthorizationState("authorized");
        if (cloudDestination === "overview") {
          setSelectedMode(null);
        } else {
          selectCloudTab(cloudDestination);
        }
        if (isLifecycleEntry) {
          setLifecycleMessage(
            "Trello read/write authorization is connected for Board Preserve.",
          );
        }
      }
    } catch (error) {
      setAuthorizationError(
        error instanceof Error
          ? error.message
          : "The connection could not be completed.",
      );
      setAuthorizationState("failed");
    }
  }

  async function clearBrowserAuthorization(): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }

    setAuthorizationState("checking");
    setAuthorizationError(null);
    setLifecycleMessage(null);
    try {
      await trelloSetup.client.clearBrowserAuthorization();
      setAuthorizedMode(null);
      setAuthorizationState("available");
      setLifecycleMessage(
        "This browser’s stored Trello token was removed. Managed Cloud Preserve access, schedules, and retained backups were not disconnected or deleted.",
      );
    } catch (error) {
      setAuthorizationError(
        error instanceof Error
          ? error.message
          : "This browser’s Trello token could not be removed.",
      );
      setAuthorizationState("failed");
    }
  }

  async function disconnectManagedCloudPreserve(): Promise<void> {
    if (!trelloSetup.client || !disconnectConfirmed) {
      return;
    }

    setCloudDisconnectState("disconnecting");
    setAuthorizationError(null);
    setLifecycleMessage(null);
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("cloud-disconnect");
      await disconnectCloudPreserve(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
      );

      let browserTokenRemoved = true;
      try {
        await trelloSetup.client.clearBrowserAuthorization();
      } catch {
        browserTokenRemoved = false;
      }
      setAuthorizedMode(null);
      setCloudState(null);
      setCloudDraft(null);
      setWorkspaceBootstrapState("disconnected");
      setDisconnectConfirmed(false);
      setAuthorizationState(browserTokenRemoved ? "available" : "authorized");
      setCloudDisconnectState("success");
      setLifecycleMessage(
        browserTokenRemoved
          ? "Cloud Preserve is disconnected. Schedules and managed access are stopped; retained backups remain only until their existing expiry dates."
          : "Cloud Preserve is disconnected and schedules are stopped. This browser’s Trello token could not be cleared, but it no longer grants managed Board Preserve access.",
      );
    } catch (error) {
      setCloudDisconnectState("failed");
      setAuthorizationError(
        error instanceof Error
          ? error.message
          : "Cloud Preserve could not be disconnected safely.",
      );
    }
  }

  async function refreshCloudConnection(): Promise<void> {
    if (!trelloSetup.client) return;
    setAuthorizationState("checking");
    setAuthorizationError(null);
    setLifecycleMessage(null);
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("cloud-settings");
      const connected = await readCloudSettings(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
      );
      setCloudState(connected);
      setWorkspaceBootstrapState("connected");
      setCloudDraft({
        retentionDays: connected.settings.retentionDays,
        retentionTestMinutes: connected.settings.retentionTestMinutes ?? null,
        schedule: connected.settings.schedule,
        scheduleTestMinutes: connected.settings.scheduleTestMinutes ?? null,
      });
      setAuthorizedMode("cloud");
      setAuthorizationState("authorized");
      setLifecycleMessage(
        "Plan, permissions, and connection status are up to date.",
      );
    } catch (error) {
      setAuthorizationState("failed");
      setAuthorizationError(
        error instanceof Error
          ? error.message
          : "Cloud Preserve could not refresh this connection.",
      );
    }
  }

  function authorizationMessage(mode: PreserveModeId): string {
    if (authorizationState === "unconfigured") {
      return "This devstage build does not have a Trello Power-Up API key.";
    }

    if (authorizationState === "client_unavailable") {
      return "Trello’s Power-Up client could not be loaded.";
    }

    if (authorizationState === "checking") {
      return "Checking existing Trello authorization…";
    }

    if (authorizationState === "authorizing") {
      return "Complete the Trello authorization popup to continue.";
    }

    if (authorizationState === "connecting") {
      return "Verifying administrator access and securing the Trello connection…";
    }

    if (authorizationState === "failed") {
      return (
        authorizationError ??
        "Trello authorization was cancelled or could not be completed. You can try again."
      );
    }

    if (mode === "local" && localReadState.status === "reading") {
      if (!localReadState.progress) {
        return "Preparing a browser-only read of this board…";
      }

      const count = localReadState.progress.itemCount;
      return `${progressLabels[localReadState.progress.stage]}… ${count.toLocaleString()} found.`;
    }

    if (mode === "local" && localReadState.status === "preparing") {
      return "Normalizing the board and preparing portable data files…";
    }

    if (mode === "local" && localReadState.status === "attachments") {
      const { completed, linked, skipped, total } = localReadState.progress;
      return `Recording attachment links… ${completed.toLocaleString()} of ${total.toLocaleString()} checked · ${linked.toLocaleString()} linked · ${skipped.toLocaleString()} skipped.`;
    }

    if (mode === "local" && localReadState.status === "building") {
      return "Writing the final manifest and assembling your ZIP…";
    }

    if (mode === "local" && localReadState.status === "cancelled") {
      return "Board read cancelled. Nothing was uploaded or downloaded.";
    }

    if (mode === "local" && localReadState.status === "failed") {
      return localReadState.message;
    }

    if (mode === "local" && localReadState.status === "complete") {
      return `${localReadState.summary.archiveFilename} was sent to your browser downloads (${formatBytes(localReadState.summary.archiveBytes)}).`;
    }

    if (authorizedMode === mode) {
      return mode === "local"
        ? "Read-only Trello access is ready for Local Export."
        : "Cloud Preserve is connected. The first managed snapshot comes next.";
    }

    if (authorizationState === "authorized") {
      return "Existing Trello authorization found. Confirm this mode to request its required permissions.";
    }

    return mode === "local"
      ? "Local Export requests read-only Trello access."
      : "Cloud Preserve requests read/write access because managed restore creates recovery copies.";
  }

  function authorizationButtonLabel(mode: PreserveModeId): string {
    if (
      mode === "local" &&
      (localReadState.status === "reading" ||
        localReadState.status === "attachments")
    ) {
      return "Cancel local export";
    }

    if (
      mode === "local" &&
      (localReadState.status === "preparing" ||
        localReadState.status === "building")
    ) {
      return "Creating archive…";
    }

    if (mode === "local" && localReadState.status === "complete") {
      return "Download a fresh copy";
    }

    if (authorizationState === "authorizing") {
      return "Waiting for Trello…";
    }

    if (authorizationState === "connecting") {
      return "Securing connection…";
    }

    if (authorizedMode === mode && mode === "cloud") {
      return "Trello connected";
    }

    if (mode === "local" && authorizedMode === "local") {
      return "Try local export again";
    }

    return mode === "local" ? "Create local export" : "Set up Cloud Preserve";
  }

  function authorizationDisabled(mode: PreserveModeId): boolean {
    return (
      trelloSetup.client === null ||
      authorizationState === "checking" ||
      authorizationState === "authorizing" ||
      authorizationState === "connecting" ||
      (mode === "cloud" && authorizedMode === mode) ||
      (mode === "local" && localReadState.status === "preparing") ||
      (mode === "local" && localReadState.status === "building") ||
      (mode === "local" &&
        localReadState.status !== "reading" &&
        !preflightComplete)
    );
  }

  function toggleAcknowledgement(id: LocalExportPreflightId): void {
    setAcknowledgements((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  async function saveCloudSettings(): Promise<boolean> {
    if (!trelloSetup.client || !cloudDraft) {
      return false;
    }

    setCloudSaveState("saving");
    setCloudSettingsError(null);

    try {
      const session =
        await trelloSetup.client.createCloudContextSession("cloud-settings");
      const updated = await updateCloudSettings(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        cloudDraft,
      );
      setCloudState(updated);
      setCloudDraft({
        retentionDays: updated.settings.retentionDays,
        retentionTestMinutes: updated.settings.retentionTestMinutes ?? null,
        schedule: updated.settings.schedule,
        scheduleTestMinutes: updated.settings.scheduleTestMinutes ?? null,
      });
      setCloudSaveState("saved");
      return true;
    } catch (error) {
      setCloudSettingsError(
        error instanceof Error
          ? error.message
          : "Cloud Preserve settings could not be saved.",
      );
      setCloudSaveState("failed");
      return false;
    }
  }

  async function monitorReattachedCloudCapture(
    snapshotId: string,
  ): Promise<void> {
    if (
      !trelloSetup.client ||
      reattachedCaptureMonitor.current === snapshotId
    ) {
      return;
    }
    reattachedCaptureMonitor.current = snapshotId;
    let failures = 0;
    try {
      for (let attempt = 0; attempt < 450; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2_000));
        try {
          const session = await trelloSetup.client.createCloudContextSession(
            "cloud-capture-status",
          );
          const status = await readCloudCaptureStatus(
            readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
            session,
            snapshotId,
          );
          failures = 0;
          setCloudCaptureJobId(status.jobId);
          setCloudCapturePurpose(status.capturePurpose);
          setCloudCaptureProgress(status.progress);
          setCloudCaptureTiming(status);
          if (status.snapshotStatus === "failed") {
            setCloudLastFailedCapture(status);
            setCloudCaptureJobId(null);
            setCloudCaptureState("failed");
            setCloudCaptureMessage(
              `This ${status.capturePurpose === "restore_safety" ? "safety backup" : "backup"} stopped safely${status.errorCode ? ` (${status.errorCode})` : ""}. No incomplete archive is available.`,
            );
            return;
          }
          if (
            status.snapshotStatus === "complete" ||
            status.snapshotStatus === "complete_with_warnings"
          ) {
            setCloudLastFailedCapture(null);
            setCloudCaptureJobId(null);
            setCloudCaptureState("complete");
            setCloudCaptureMessage(
              `${status.capturePurpose === "restore_safety" ? "Safety backup" : "Backup"} complete${status.snapshotStatus === "complete_with_warnings" ? " with warnings" : ""}. It is ready in Backups.`,
            );
            if (status.capturePurpose !== "restore_safety") {
              selectRestoreSnapshot(status.snapshotId);
            }
            await loadCloudSnapshots();
            return;
          }
          if (
            status.controlState === "canceled" ||
            status.jobStatus === "canceled"
          ) {
            setCloudCaptureJobId(null);
            setCloudCaptureState("cancelled");
            setCloudCaptureMessage(
              "Backup cancelled safely. No incomplete archive is available.",
            );
            return;
          }
          if (
            status.controlState === "paused" ||
            status.jobStatus === "paused"
          ) {
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: undefined,
            }));
            setCloudCaptureState("paused");
            setCloudCaptureMessage(
              "Backup paused at a safe checkpoint. Resume when you are ready.",
            );
            await new Promise((resolve) => window.setTimeout(resolve, 8_000));
            continue;
          }
          if (
            status.controlState === "pause_requested" ||
            status.controlState === "cancel_requested"
          ) {
            const action =
              status.controlState === "pause_requested" ? "pause" : "cancel";
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: action,
            }));
            setCloudCaptureState("processing");
            setCloudCaptureMessage(
              action === "pause"
                ? "Pause requested. The current operation will finish safely first."
                : "Cancellation requested. The current operation will finish safely; no incomplete archive will be downloadable.",
            );
            continue;
          }
          setCloudJobPendingActions((current) => ({
            ...current,
            [status.jobId]: undefined,
          }));
          setCloudCaptureState("processing");
          setCloudCaptureMessage(
            {
              building_archive:
                "Board captured. Building the downloadable archive…",
              capturing_attachments:
                "Board captured. Securing retrievable attachments…",
              capturing_structure:
                "Reading the board’s structure and activity…",
              pending: "Backup queued. Waiting for a secure capture worker…",
            }[status.snapshotStatus],
          );
        } catch {
          failures += 1;
          if (failures >= 3) {
            setCloudCaptureMessage(
              "The backup is still running, but live status is temporarily unavailable. Refresh shortly.",
            );
            return;
          }
        }
      }
    } finally {
      if (reattachedCaptureMonitor.current === snapshotId) {
        reattachedCaptureMonitor.current = null;
      }
    }
  }

  async function reattachActiveCloudCapture(): Promise<void> {
    if (!trelloSetup.client || !cloudState) {
      return;
    }
    try {
      const session = await trelloSetup.client.createCloudContextSession(
        "cloud-active-captures",
      );
      const active = await readActiveCloudCaptures(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
      );
      const newest = active.captures[0];
      if (!newest) {
        return;
      }
      setCloudCaptureJobId(newest.jobId);
      setCloudCapturePurpose(newest.capturePurpose);
      setCloudCaptureProgress(newest.progress);
      setCloudCaptureTiming(newest);
      setCloudJobPendingActions((current) => ({
        ...current,
        [newest.jobId]:
          newest.controlState === "pause_requested"
            ? "pause"
            : newest.controlState === "cancel_requested"
              ? "cancel"
              : undefined,
      }));
      setCloudCaptureState(
        newest.controlState === "paused" ? "paused" : "processing",
      );
      setCloudCaptureMessage(
        newest.controlState === "paused"
          ? `${newest.capturePurpose === "restore_safety" ? "Safety backup" : "Backup"} paused at a safe checkpoint. Resume when you are ready.`
          : newest.controlState === "pause_requested"
            ? "Pause requested. The current operation will finish safely first."
            : newest.controlState === "cancel_requested"
              ? "Cancellation requested. The current operation will finish safely; no incomplete archive will be downloadable."
              : `Reattached to the active ${newest.capturePurpose === "restore_safety" ? "safety backup" : "backup"} on this board.`,
      );
      void monitorReattachedCloudCapture(newest.snapshotId);
    } catch {
      // The normal backup and history controls remain usable when discovery is
      // temporarily unavailable.
    }
  }

  async function runCloudCapture(): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }
    setCloudCaptureState("submitting");
    setCloudLastFailedCapture(null);
    setCloudCapturePurpose("standard");
    setCloudCaptureMessage(null);
    setCloudCaptureProgress({ completed: 0, total: null });
    cloudCaptureProgressSignature.current = null;
    setCloudCaptureProgressObservedAt(Date.now());
    setCloudCaptureTiming(null);
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("cloud-capture");
      cloudCaptureIdempotencyKey.current ??= crypto.randomUUID();
      const result = await startCloudCapture(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        cloudCaptureIdempotencyKey.current,
      );
      setCloudCaptureJobId(result.jobId);
      setCloudCaptureState("accepted");
      setCloudCaptureTiming({
        controlState: "active",
        jobStatus: "pending",
        snapshotStatus: "pending",
        startedAt: null,
      });
      setCloudState((current) =>
        current ? { ...current, trial: result.trial } : current,
      );
      setCloudCaptureMessage(
        result.status === "accepted"
          ? "Backup accepted. Board Preserve will keep working after this modal closes."
          : "This backup is already in progress and will resume safely.",
      );

      let consecutiveStatusFailures = 0;
      let activePollAttempts = 0;
      while (activePollAttempts < 450) {
        await new Promise((resolve) => window.setTimeout(resolve, 2_000));
        try {
          const statusSession =
            await trelloSetup.client.createCloudContextSession(
              "cloud-capture-status",
            );
          const status = await readCloudCaptureStatus(
            readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
            statusSession,
            result.snapshotId,
          );
          consecutiveStatusFailures = 0;
          setCloudCaptureProgress(status.progress);
          setCloudCapturePurpose(status.capturePurpose);
          setCloudCaptureTiming(status);

          if (status.snapshotStatus === "failed") {
            setCloudLastFailedCapture(status);
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: undefined,
            }));
            cloudCaptureIdempotencyKey.current = null;
            setCloudCaptureJobId(null);
            setCloudCaptureState("failed");
            setCloudCaptureMessage(
              `This backup stopped safely${status.errorCode ? ` (${status.errorCode})` : ""}. No incomplete archive is available.`,
            );
            return;
          }

          if (
            status.snapshotStatus === "complete" ||
            status.snapshotStatus === "complete_with_warnings"
          ) {
            setCloudLastFailedCapture(null);
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: undefined,
            }));
            cloudCaptureIdempotencyKey.current = null;
            setCloudCaptureJobId(null);
            setCloudCaptureState("complete");
            setCloudCaptureMessage(
              status.snapshotStatus === "complete"
                ? "Backup complete. It is ready in Backups."
                : "Backup complete with warnings. Review it in Backups.",
            );
            selectRestoreSnapshot(status.snapshotId);
            setCloudTab("history");
            await loadCloudSnapshots();
            return;
          }

          if (status.controlState === "cancel_requested") {
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: "cancel",
            }));
            setCloudCaptureState("processing");
            setCloudCaptureMessage(
              "Cancellation requested. The current operation will finish safely; no incomplete archive will be downloadable.",
            );
            activePollAttempts += 1;
            continue;
          }

          if (status.controlState === "pause_requested") {
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: "pause",
            }));
            setCloudCaptureState("processing");
            setCloudCaptureMessage(
              "Pause requested. The current operation will finish safely first.",
            );
            activePollAttempts += 1;
            continue;
          }

          if (
            status.controlState === "canceled" ||
            status.jobStatus === "canceled"
          ) {
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: undefined,
            }));
            cloudCaptureIdempotencyKey.current = null;
            setCloudCaptureJobId(null);
            setCloudCaptureState("cancelled");
            setCloudCaptureMessage(
              "Backup cancelled safely. No incomplete archive is available.",
            );
            return;
          }

          if (
            status.controlState === "paused" ||
            status.jobStatus === "paused"
          ) {
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: undefined,
            }));
            setCloudCaptureState("paused");
            setCloudCaptureMessage(
              "Backup paused at a safe checkpoint. Resume when you are ready.",
            );
            await new Promise((resolve) => window.setTimeout(resolve, 8_000));
            continue;
          }

          if (status.controlState === "active") {
            setCloudJobPendingActions((current) => ({
              ...current,
              [status.jobId]: undefined,
            }));
          }

          activePollAttempts += 1;

          setCloudCaptureState("processing");
          setCloudCaptureMessage(
            {
              building_archive:
                "Board captured. Building the downloadable archive…",
              capturing_attachments:
                "Board captured. Securing retrievable attachments…",
              capturing_structure:
                "Reading the board’s structure and activity…",
              pending: "Backup queued. Waiting for a secure capture worker…",
            }[status.snapshotStatus],
          );
        } catch {
          activePollAttempts += 1;
          consecutiveStatusFailures += 1;
          if (consecutiveStatusFailures >= 3) {
            setCloudCaptureState("processing");
            setCloudCaptureMessage(
              "The backup is still running, but live status is temporarily unavailable. Check History & recovery shortly.",
            );
            return;
          }
        }
      }

      setCloudCaptureState("processing");
      setCloudCaptureMessage(
        "The backup is still running. You can close this modal and check History & recovery later.",
      );
    } catch (error) {
      setCloudCaptureState("failed");
      setCloudCaptureMessage(
        error instanceof Error
          ? error.message
          : "The managed backup could not be started.",
      );
    }
  }

  async function controlActiveCloudJob(
    jobId: string,
    action: JobControlAction["action"],
    jobType: "capture" | "restore",
  ): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }
    setCloudJobControl({ action, jobId });
    setCloudJobControlMessages((current) => ({ ...current, [jobId]: "" }));
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("job-control");
      const result = await controlCloudJob(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        jobId,
        action,
      );
      const message =
        action === "pause"
          ? result.controlState === "paused"
            ? "Paused at a safe checkpoint."
            : "Pause requested. The current operation will finish safely first."
          : action === "resume"
            ? "Resume requested. Work will continue from the saved checkpoint."
            : action === "retry"
              ? "Retry queued. Successful items will not be recreated; only failed or previously aborted operations will run."
              : jobType === "restore"
                ? result.controlState === "canceled"
                  ? "Cancelled. Already-created Trello items remain; no further operations will be started."
                  : "Cancellation requested. Already-created Trello items remain; only remaining operations will stop."
                : result.controlState === "canceled"
                  ? "Backup cancelled safely. No incomplete archive is available."
                  : "Cancellation requested. The current operation will finish safely; no incomplete archive will be downloadable.";
      setCloudJobPendingActions((current) => ({
        ...current,
        [jobId]:
          result.controlState === "pause_requested"
            ? "pause"
            : result.controlState === "cancel_requested"
              ? "cancel"
              : undefined,
      }));
      setCloudJobControlMessages((current) => ({
        ...current,
        [jobId]: message,
      }));
      if (jobType === "capture") {
        if (action === "pause") {
          setCloudCaptureState(
            result.controlState === "paused" ? "paused" : "processing",
          );
          setCloudCaptureMessage(message);
        } else if (action === "resume") {
          setCloudCaptureState("processing");
          setCloudCaptureMessage(message);
        } else if (result.controlState === "canceled") {
          cloudCaptureIdempotencyKey.current = null;
          setCloudCaptureJobId(null);
          setCloudCaptureState("cancelled");
          setCloudCaptureMessage(message);
        } else {
          setCloudCaptureMessage(message);
        }
      } else {
        const history = await loadCloudRestores(true);
        const latest = history?.restores.find(
          (restore) => restore.jobId === jobId,
        );
        if (
          latest &&
          [
            "paused",
            "canceled",
            "complete",
            "complete_with_warnings",
            "failed",
          ].includes(latest.status)
        ) {
          setCloudJobPendingActions((current) => ({
            ...current,
            [jobId]: undefined,
          }));
        }
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "The job control could not be applied safely.";
      setCloudJobControlMessages((current) => ({
        ...current,
        [jobId]: message,
      }));
      setCloudJobPendingActions((current) => ({
        ...current,
        [jobId]: undefined,
      }));
      if (jobType === "capture") {
        setCloudCaptureMessage(message);
      }
    } finally {
      setCloudJobControl(null);
    }
  }

  async function attachReplacementZipAndRetry(
    jobId: string,
    importId: string,
  ): Promise<void> {
    if (!trelloSetup.client) return;
    setCloudJobControlMessages((current) => ({
      ...current,
      [jobId]: "Attaching the verified replacement ZIP…",
    }));
    try {
      const session = await trelloSetup.client.createCloudContextSession(
        "restore-import-rebind",
      );
      await rebindUploadedRestoreJob(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        importId,
        jobId,
      );
      setRestoreReuploadJobId(null);
      await controlActiveCloudJob(jobId, "retry", "restore");
    } catch (error) {
      setCloudJobControlMessages((current) => ({
        ...current,
        [jobId]:
          error instanceof Error
            ? error.message
            : "The replacement ZIP could not be attached safely.",
      }));
    }
  }

  async function loadCloudSnapshots(silent = false): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }
    if (!silent) {
      setCloudHistoryState("loading");
    }
    setCloudHistoryError(null);
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("cloud-history");
      const history = await readCloudHistory(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
      );
      setCloudHistory(history);
      setCloudState((current) => applyHistoryScheduleState(current, history));
      if (
        uploadedRestoreSnapshotId === null ||
        selectedRestoreSnapshotId !== uploadedRestoreSnapshotId
      ) {
        const selection = resolveRestoreSnapshotSelection(
          history.snapshots.map(({ id }) => id),
          selectedRestoreSnapshotId,
        );
        if (selection !== selectedRestoreSnapshotId) {
          selectRestoreSnapshot(selection);
        }
      }
      setCloudHistoryState("ready");
    } catch (error) {
      setCloudHistoryError(
        error instanceof Error
          ? error.message
          : "Backup history could not be loaded.",
      );
      setCloudHistoryState("failed");
    }
  }

  async function deleteSnapshotFromHistory(
    snapshotId: string,
    confirmation: string,
  ): Promise<void> {
    if (!trelloSetup.client || cloudDeleteSnapshotId !== null) return;
    setCloudDeleteSnapshotId(snapshotId);
    setCloudDeleteError(null);
    setCloudDownloadMessage(null);
    try {
      const session = await trelloSetup.client.createCloudContextSession(
        "cloud-snapshot-delete",
      );
      await deleteCloudSnapshot(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        snapshotId,
        confirmation,
      );
      setCloudHistory((current) =>
        current
          ? {
              ...current,
              snapshots: current.snapshots.filter(
                ({ id }) => id !== snapshotId,
              ),
            }
          : current,
      );
      setCloudWarningReports((current) => {
        const next = { ...current };
        delete next[snapshotId];
        return next;
      });
      if (selectedRestoreSnapshotId === snapshotId) {
        selectRestoreSnapshot(null);
      }
      setCloudDownloadMessage(
        "Backup deleted. Its stored files are being permanently removed.",
      );
      await loadCloudSnapshots(true);
    } catch (error) {
      setCloudDeleteError(
        error instanceof Error
          ? error.message
          : "The backup could not be deleted safely.",
      );
    } finally {
      setCloudDeleteSnapshotId(null);
    }
  }

  async function loadSnapshotWarnings(snapshotId: string): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }
    setCloudWarningReports((current) => ({
      ...current,
      [snapshotId]: {
        error: null,
        report: current[snapshotId]?.report ?? null,
        state: "loading",
      },
    }));
    try {
      const session = await trelloSetup.client.createCloudContextSession(
        "cloud-history-warnings",
      );
      const report = await readCloudSnapshotWarnings(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        snapshotId,
      );
      setCloudWarningReports((current) => ({
        ...current,
        [snapshotId]: { error: null, report, state: "ready" },
      }));
    } catch (error) {
      setCloudWarningReports((current) => ({
        ...current,
        [snapshotId]: {
          error:
            error instanceof Error
              ? error.message
              : "The warning report could not be loaded.",
          report: null,
          state: "failed",
        },
      }));
    }
  }

  async function downloadSnapshot(
    snapshotId: string,
    capturedAt: string,
    expectedBytes: number,
  ): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }

    setCloudDownloadSnapshotId(snapshotId);
    setCloudDownloadState("downloading");
    setCloudDownloadMessage("Choose where to save this private ZIP archive.");
    try {
      const timezoneOffsetMinutes = timezoneOffsetMinutesForCapture(capturedAt);
      const fallbackFilename = createCloudArchiveFilename(
        snapshotId,
        capturedAt,
        timezoneOffsetMinutes,
      );
      // Preparing the target must be the first awaited browser action so a
      // supported Save As picker retains the original click activation.
      const target = await prepareDownloadTarget(fallbackFilename);
      setCloudDownloadMessage("Authorizing this download against Trello…");
      const session =
        await trelloSetup.client.createCloudContextSession("cloud-history");
      const apiUrl = readCloudApiUrl(
        import.meta.env.VITE_BOARD_PRESERVE_API_URL,
      );
      if (
        target.kind === "automatic" &&
        expectedBytes > MAX_MEMORY_DOWNLOAD_BYTES
      ) {
        setCloudDownloadMessage(
          "Starting a browser-managed download inside Trello…",
        );
        startNativeCloudSnapshotDownload(
          apiUrl,
          session,
          snapshotId,
          timezoneOffsetMinutes,
        );
        setCloudDownloadState("complete");
        setCloudDownloadMessage(
          "The secure archive download was sent to your browser.",
        );
        return;
      }
      const response = await downloadCloudSnapshot(
        apiUrl,
        session,
        snapshotId,
        timezoneOffsetMinutes,
      );
      const result = await downloadResponse(response, fallbackFilename, {
        expectedBytes,
        onStorageMode: (mode) => {
          setCloudDownloadMessage(
            mode === "save_picker"
              ? "Streaming the archive directly to your chosen file…"
              : "Preparing this small archive in browser memory…",
          );
        },
        target,
      });
      setCloudDownloadState("complete");
      setCloudDownloadMessage(`${result.filename} was sent to your downloads.`);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setCloudDownloadState("idle");
        setCloudDownloadMessage(
          "Download cancelled. The retained backup was not changed.",
        );
        return;
      }
      setCloudDownloadState("failed");
      setCloudDownloadMessage(
        error instanceof Error
          ? error.message
          : "The backup archive could not be downloaded.",
      );
    }
  }

  function selectRestoreSnapshot(snapshotId: string | null): void {
    setSelectedRestoreSnapshotId(snapshotId);
    setCloudRestorePreviewState("idle");
    setCloudRestorePreview(null);
    setCloudRestorePreviewError(null);
    setCloudRestoreConfirmState("idle");
    setCloudRestoreConfirmError(null);
    setCloudRestorePreparation(null);
    setCloudRestoreAcknowledgements({
      duplicateRestore: false,
      fidelity: false,
      newBoard: false,
      noMutation: false,
    });
    cloudRestoreIdempotencyKey.current = null;
  }

  async function previewSelectedRestore(
    snapshotIdOverride?: string,
  ): Promise<void> {
    const snapshotId = snapshotIdOverride ?? selectedRestoreSnapshotId;
    if (!trelloSetup.client || snapshotId === null) {
      return;
    }
    setCloudRestorePreviewState("loading");
    setCloudRestorePreviewError(null);
    setCloudRestoreConfirmState("idle");
    setCloudRestoreConfirmError(null);
    setCloudRestorePreparation(null);
    setCloudRestoreAcknowledgements({
      duplicateRestore: false,
      fidelity: false,
      newBoard: false,
      noMutation: false,
    });
    cloudRestoreIdempotencyKey.current = null;
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("restore-preview");
      const preview = await previewCloudRestore(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        snapshotId,
      );
      setCloudRestorePreview(preview);
      setCloudRestorePreviewState("ready");
    } catch (error) {
      setCloudRestorePreviewError(
        error instanceof Error
          ? error.message
          : "The restore preview could not be prepared.",
      );
      setCloudRestorePreviewState("failed");
    }
  }

  async function confirmSelectedRestore(): Promise<void> {
    if (
      !trelloSetup.client ||
      !cloudRestorePreview ||
      !cloudRestoreAcknowledgements.fidelity ||
      !cloudRestoreAcknowledgements.newBoard ||
      !cloudRestoreAcknowledgements.noMutation ||
      (cloudRestorePreview.duplicateRestore.requiresExplicitOverride &&
        !cloudRestoreAcknowledgements.duplicateRestore)
    ) {
      return;
    }
    setCloudRestoreConfirmState("submitting");
    setCloudRestoreConfirmError(null);
    cloudRestoreIdempotencyKey.current ??= crypto.randomUUID();
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("restore-confirm");
      const preparation = await confirmCloudRestore(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        cloudRestorePreview,
        cloudRestoreIdempotencyKey.current,
        undefined,
        cloudRestorePreview.duplicateRestore.requiresExplicitOverride,
      );
      setCloudRestorePreparation(preparation);
      setCloudRestoreConfirmState("submitted");
      setCloudRestoreHistoryState("idle");
      try {
        const settingsSession =
          await trelloSetup.client.createCloudContextSession("cloud-settings");
        const refreshed = await readCloudSettings(
          readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
          settingsSession,
        );
        setCloudState(refreshed);
      } catch {
        // The confirmed restore remains authoritative. A later normal refresh
        // will reconcile the one-use Trial ZIP state if this read is lost.
      }
      void loadCloudRestores();
    } catch (error) {
      setCloudRestoreConfirmError(
        error instanceof Error
          ? error.message
          : "The restore could not be confirmed safely.",
      );
      setCloudRestoreConfirmState("failed");
    }
  }

  async function loadCloudRestores(
    silent = false,
  ): Promise<CloudRestoreHistory | null> {
    if (!trelloSetup.client) {
      return null;
    }
    if (!silent) {
      setCloudRestoreHistoryState("loading");
    }
    setCloudRestoreHistoryError(null);
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("restore-status");
      const history = await readCloudRestoreHistory(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
      );
      setCloudRestoreHistory(history);
      setCloudJobPendingActions((current) => {
        let changed = false;
        const next = { ...current };
        for (const restore of history.restores) {
          const action = current[restore.jobId];
          if (
            restore.controlState === "pause_requested" ||
            restore.controlState === "cancel_requested"
          ) {
            const requestedAction =
              restore.controlState === "pause_requested" ? "pause" : "cancel";
            if (action !== requestedAction) {
              next[restore.jobId] = requestedAction;
              changed = true;
            }
            continue;
          }
          const terminal = [
            "canceled",
            "complete",
            "complete_with_warnings",
            "failed",
          ].includes(restore.status);
          if (
            (action === "pause" && (restore.status === "paused" || terminal)) ||
            (action === "cancel" && terminal)
          ) {
            next[restore.jobId] = undefined;
            changed = true;
          }
        }
        return changed ? next : current;
      });
      setCloudRestoreHistoryState("ready");
      return history;
    } catch (error) {
      setCloudRestoreHistoryError(
        error instanceof Error
          ? error.message
          : "Restore reports could not be loaded.",
      );
      setCloudRestoreHistoryState("failed");
      return null;
    }
  }

  async function refreshCloudHistory(): Promise<void> {
    await Promise.all([loadCloudSnapshots(), loadCloudRestores()]);
  }

  async function loadCloudRestoreReport(
    jobId: string,
    after = 0,
  ): Promise<void> {
    if (!trelloSetup.client) {
      return;
    }
    setCloudRestoreReportJobId(jobId);
    setCloudRestoreReportState("loading");
    setCloudRestoreReportError(null);
    try {
      const session =
        await trelloSetup.client.createCloudContextSession("restore-status");
      const report = await readCloudRestoreReport(
        readCloudApiUrl(import.meta.env.VITE_BOARD_PRESERVE_API_URL),
        session,
        jobId,
        after,
      );
      setCloudRestoreReport((current) =>
        after > 0 && current?.jobId === report.jobId
          ? {
              ...report,
              operations: [...current.operations, ...report.operations],
            }
          : report,
      );
      setCloudRestoreReportState("ready");
    } catch (error) {
      setCloudRestoreReportError(
        error instanceof Error
          ? error.message
          : "The restore report could not be loaded.",
      );
      setCloudRestoreReportState("failed");
    }
  }

  function selectCloudTab(tab: CloudTab): void {
    setCloudTab(tab);
    if (tab === "history") {
      setRecoveryIntent(null);
    }
    if (tab !== "plan") {
      setPlanView("usage");
    }
    if (tab === "backup") {
      void reattachActiveCloudCapture();
    }
    if (
      (tab === "history" || tab === "recover") &&
      cloudHistoryState === "idle"
    ) {
      void loadCloudSnapshots();
    }
    if (
      (tab === "history" || tab === "recover") &&
      cloudRestoreHistoryState === "idle"
    ) {
      void loadCloudRestores();
    }
  }

  const activeRestore = cloudRestoreHistory?.restores.find(({ status }) =>
    ["pending", "running", "paused"].includes(status),
  );
  const hasActiveRestore = activeRestore !== undefined;
  const cloudCaptureActive = [
    "accepted",
    "paused",
    "processing",
    "submitting",
  ].includes(cloudCaptureState);
  const cloudCapturePercent = cloudCaptureProgress
    ? progressPercentage(
        cloudCaptureProgress.completed,
        cloudCaptureProgress.total,
      )
    : null;
  const sortedRestoreReports = newestRestoreReports(
    cloudRestoreHistory?.restores ?? [],
  );
  const visibleRestoreReports = selectVisibleRestoreReports(
    sortedRestoreReports,
    restoreReportsExpanded,
  );

  function openRecoveryJob(jobId: string): void {
    setSelectedMode("cloud");
    setJobsFilter("recoveries");
    setRestoreReportsExpanded(true);
    selectCloudTab("history");
    void loadCloudRestoreReport(jobId);
  }
  const selectedInPlaceRestoreSource = (() => {
    const retained = cloudHistory?.snapshots.find(
      ({ id }) => id === selectedRestoreSnapshotId,
    );
    if (retained) return retained;
    if (
      selectedRestoreSnapshotId === uploadedRestoreSnapshotId &&
      cloudRestorePreview?.snapshot.snapshotId === selectedRestoreSnapshotId
    ) {
      return {
        capturedAt: cloudRestorePreview.snapshot.capturedAt,
        id: selectedRestoreSnapshotId,
      };
    }
    return null;
  })();

  useEffect(() => {
    if (cloudTab !== "history" || !hasActiveRestore) {
      return;
    }

    const interval = window.setInterval(() => {
      void loadCloudRestores(true);
    }, 2_000);

    return () => {
      window.clearInterval(interval);
    };
  }, [cloudTab, hasActiveRestore]);

  useEffect(() => {
    if (cloudState === null) {
      return;
    }

    let refreshing = false;
    return startCloudHistoryRefresh({
      refresh: () => {
        if (refreshing) {
          return;
        }
        refreshing = true;
        void Promise.all([
          loadCloudSnapshots(true),
          loadCloudRestores(true),
          cloudCaptureJobId === null
            ? reattachActiveCloudCapture()
            : Promise.resolve(),
        ]).finally(() => {
          refreshing = false;
        });
      },
    });
  }, [
    cloudCaptureJobId,
    cloudState,
    selectedRestoreSnapshotId,
    uploadedRestoreSnapshotId,
  ]);

  return (
    <main className="shell">
      <section className="capture-card" aria-label="Board Preserve">
        {isLifecycleEntry ? (
          <header className="product-header">
            <div className="archive-mark">
              <ArchiveIcon />
            </div>
            <div>
              <p className="product-name">Board Preserve</p>
              <h1 id="page-title">
                {lifecycleEntry === "authorization"
                  ? "Authorize Board Preserve"
                  : lifecycleEntry === "settings"
                    ? "Power-Up settings"
                    : selectedMode === "local"
                      ? "Local Export"
                      : "Cloud Preserve"}
              </h1>
              <p className="header-summary">
                {isLifecycleEntry
                  ? "Review and manage this browser’s Trello authorization."
                  : selectedMode === "local"
                    ? "Create a portable ZIP without sending board content to Nodestone."
                    : "Back up, inspect and safely recover this board."}
              </p>
            </div>
          </header>
        ) : null}

        {!isLifecycleEntry &&
        workspaceBootstrapState !== "checking" &&
        workspaceBootstrapState !== "unavailable" ? (
          <WorkspaceContextHeader
            historyPending={
              cloudState !== null &&
              (cloudHistoryState === "idle" || cloudHistoryState === "loading")
            }
            cloudState={cloudState}
            onPlan={() => {
              setSelectedMode("cloud");
              setPlanView("usage");
              selectCloudTab("plan");
            }}
          />
        ) : null}

        {!isLifecycleEntry &&
        (workspaceBootstrapState === "checking" ||
          workspaceBootstrapState === "unavailable") ? (
          <WorkspaceBootstrap
            failed={workspaceBootstrapState === "unavailable"}
            onRetry={() => {
              void hydrateWorkspaceConnection();
            }}
          />
        ) : null}

        {!isLifecycleEntry &&
        selectedMode !== null &&
        workspaceBootstrapState === "connected" &&
        !(selectedMode === "cloud" && cloudTab === "backup") ? (
          <div className="global-active-work">
            <ActiveWork
              captureMessage={cloudCaptureMessage}
              capturePercent={cloudCapturePercent}
              captureState={cloudCaptureState}
              onOpenActiveBackup={() => {
                setSelectedMode("cloud");
                selectCloudTab("backup");
              }}
              onOpenActiveRestore={() => {
                if (activeRestore) openRecoveryJob(activeRestore.jobId);
              }}
              restoreActive={hasActiveRestore}
            />
          </div>
        ) : null}

        {!isLifecycleEntry &&
        selectedMode === null &&
        workspaceBootstrapState !== "checking" &&
        workspaceBootstrapState !== "unavailable" ? (
          <WorkspaceOverview
            captureMessage={cloudCaptureMessage}
            capturePercent={cloudCapturePercent}
            captureState={cloudCaptureState}
            cloudHistory={cloudHistory}
            cloudRestoreHistory={cloudRestoreHistory}
            cloudState={cloudState}
            historyPending={
              cloudState !== null &&
              (cloudHistoryState === "idle" || cloudHistoryState === "loading")
            }
            onOpenBackup={() => {
              setSelectedMode("cloud");
              selectCloudTab("backup");
            }}
            onBackups={() => {
              setSelectedMode("cloud");
              setJobsFilter("all");
              selectCloudTab("history");
            }}
            onLocalExport={() => {
              setSelectedMode("local");
            }}
            onOpenActiveBackup={() => {
              setSelectedMode("cloud");
              selectCloudTab("backup");
            }}
            onOpenActiveRestore={() => {
              if (activeRestore) openRecoveryJob(activeRestore.jobId);
            }}
            onOpenRecoveryJob={openRecoveryJob}
            onPlan={() => {
              setSelectedMode("cloud");
              setPlanView("usage");
              selectCloudTab("plan");
            }}
            onRecovery={() => {
              setSelectedMode("cloud");
              selectCloudTab("recover");
            }}
            onSchedule={() => {
              setSelectedMode("cloud");
              selectCloudTab("schedule");
            }}
            onSettings={() => {
              setSelectedMode("cloud");
              selectCloudTab("settings");
            }}
            onSetupCloud={() => {
              setSelectedMode("cloud");
              selectCloudTab("backup");
            }}
            restoreActive={hasActiveRestore}
          />
        ) : null}

        {selectedMode === "local" ? (
          <section className="mode-detail" aria-labelledby="local-title">
            <TaskScreenHeader
              onBack={() => {
                setSelectedMode(null);
              }}
              subtitle="Built in this browser. Board content is never sent to Nodestone."
              title="Create a local ZIP"
              titleId="local-title"
            >
              <span className="privacy-seal">Browser only</span>
            </TaskScreenHeader>

            <fieldset className="preflight-list">
              <legend className="sr-only">
                Confirm Local Export limitations
              </legend>
              {localExportPreflight.map((item) => (
                <label key={item.id}>
                  <input
                    checked={acknowledgements.has(item.id)}
                    onChange={() => {
                      toggleAcknowledgement(item.id);
                    }}
                    type="checkbox"
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </fieldset>

            <div className="detail-footer">
              <div>
                <p aria-live="polite" className="status-message">
                  {preflightComplete
                    ? authorizationMessage("local")
                    : `${localExportPreflight.length - acknowledgements.size} of ${localExportPreflight.length} confirmations remaining.`}
                </p>
              </div>
              <button
                type="button"
                disabled={authorizationDisabled("local")}
                onClick={() => {
                  if (
                    localReadState.status === "reading" ||
                    localReadState.status === "attachments"
                  ) {
                    cancelLocalRead();
                  } else if (authorizedMode === "local") {
                    void inspectLocalBoard();
                  } else {
                    void authorizeTrello("local");
                  }
                }}
              >
                {authorizationButtonLabel("local")}
              </button>
            </div>
            {localReadState.status === "complete" ? (
              <section
                aria-label="Board inspection summary"
                className="inspection-summary"
              >
                <div>
                  <strong>{localReadState.summary.lists}</strong>
                  <span>Lists</span>
                </div>
                <div>
                  <strong>{localReadState.summary.cards}</strong>
                  <span>Cards</span>
                </div>
                <div>
                  <strong>{localReadState.summary.checklists}</strong>
                  <span>Checklists</span>
                </div>
                <div>
                  <strong>{localReadState.summary.customFields}</strong>
                  <span>Custom fields</span>
                </div>
                <div>
                  <strong>{localReadState.summary.activities}</strong>
                  <span>Card activity</span>
                </div>
                <div>
                  <strong>{localReadState.summary.attachments}</strong>
                  <span>Attachments</span>
                </div>
                <p className="preparation-summary">
                  <strong>
                    {localReadState.summary.archiveFiles} files ·{" "}
                    {formatBytes(localReadState.summary.archiveBytes)}
                  </strong>
                  <span>
                    {localReadState.summary.linkOnlyAttachments} attachment
                    {localReadState.summary.linkOnlyAttachments === 1
                      ? " link"
                      : " links"}{" "}
                    retained · {localReadState.summary.skippedAttachments}{" "}
                    skipped
                    {localReadState.summary.warnings > 0
                      ? ` · ${localReadState.summary.warnings} warning${localReadState.summary.warnings === 1 ? "" : "s"} recorded`
                      : ""}
                  </span>
                </p>
              </section>
            ) : null}
          </section>
        ) : selectedMode === "cloud" ? (
          <section
            className="mode-detail cloud-detail"
            aria-labelledby="cloud-title"
          >
            {!isLifecycleEntry ? (
              <TaskScreenHeader
                backLabel={
                  cloudTab === "plan" && planView === "compare"
                    ? "Back to plan and usage"
                    : cloudTab === "recover" && recoveryIntent !== null
                      ? "Back to recovery"
                      : (cloudState?.board.name ?? "Back to board overview")
                }
                onBack={() => {
                  if (cloudTab === "plan" && planView === "compare") {
                    setPlanView("usage");
                    return;
                  }
                  if (cloudTab === "recover" && recoveryIntent !== null) {
                    setRecoveryIntent(null);
                    return;
                  }
                  setSelectedMode(null);
                }}
                subtitle={
                  cloudTab === "plan"
                    ? planView === "compare"
                      ? `Plans cover every protected board in ${cloudState?.workspace.name ?? "this Workspace"}.`
                      : cloudState
                        ? cloudState.entitlements.effectivePlan === "free"
                          ? "Trial · one free managed backup for this Workspace"
                          : `${cloudPlanLabel(cloudState.entitlements.effectivePlan)} · ${cloudState.workspace.name}`
                        : "Review protection, storage and recovery access for this Workspace."
                    : !cloudState
                      ? "Keep recovery points after this Trello tab closes."
                      : cloudTab === "backup"
                        ? cloudCaptureActive
                          ? `${cloudCaptureTiming?.startedAt ? `Started ${new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date(cloudCaptureTiming.startedAt))}` : "Starting now"}. You can close this window; the backup continues.`
                          : "Create a recovery point for this board now."
                        : cloudTab === "schedule"
                          ? "Changes apply to future backups. Existing expiry dates stay as they are."
                          : cloudTab === "history"
                            ? `${(cloudHistory?.snapshots.length ?? 0) + (cloudRestoreHistory?.restores.length ?? 0)} recent jobs · backup files follow their retention policy · recovery reports are kept for 30 days`
                            : cloudTab === "recover"
                              ? recoveryIntent === "zip"
                                ? "Upload and verify a Board Preserve ZIP before choosing a recovery action."
                                : recoveryIntent === "new_board"
                                  ? "Create a separate private board and leave the original unchanged."
                                  : recoveryIntent === "selective"
                                    ? "Recreate one missing card or list without overwriting existing content."
                                    : recoveryIntent === "compare"
                                      ? "Inspect differences between retained backups before recovering."
                                      : recoveryIntent === "in_place"
                                        ? "Compare first, create a safety backup, then explicitly confirm each change."
                                        : recoveryIntent === "reports"
                                          ? "Review current and recent recovery jobs object by object."
                                          : "Safe routes first. Nothing is written to Trello until you confirm."
                              : "Manage Trello access and this Workspace’s Cloud Preserve connection."
                }
                title={
                  cloudTab === "plan"
                    ? planView === "compare"
                      ? "Choose a plan"
                      : "Plan and usage"
                    : !cloudState
                      ? "Set up Cloud Preserve"
                      : cloudTab === "backup"
                        ? cloudCaptureState === "paused"
                          ? "Backup paused"
                          : cloudCaptureActive
                            ? "Backing up this board"
                            : "Backup this board"
                        : cloudTab === "schedule"
                          ? "Backup schedule"
                          : cloudTab === "history"
                            ? "Jobs"
                            : cloudTab === "recover"
                              ? recoveryIntent === "zip"
                                ? "Restore from ZIP"
                                : recoveryIntent === "new_board"
                                  ? "Create a recovery board"
                                  : recoveryIntent === "selective"
                                    ? "Recover one card or list"
                                    : recoveryIntent === "compare"
                                      ? "Compare backups"
                                      : recoveryIntent === "in_place"
                                        ? "Restore onto this board"
                                        : recoveryIntent === "reports"
                                          ? "Restore reports"
                                          : "Recover something"
                              : "Settings"
                }
                titleId="cloud-title"
              >
                {cloudTab === "history" ? (
                  <button
                    className="task-screen-refresh"
                    disabled={cloudHistoryState === "loading"}
                    onClick={() => {
                      void refreshCloudHistory();
                    }}
                    type="button"
                  >
                    {cloudHistoryState === "loading"
                      ? "Refreshing…"
                      : "Refresh"}
                  </button>
                ) : null}
              </TaskScreenHeader>
            ) : null}

            {cloudState && cloudTab === "history" ? (
              <div
                className="jobs-filter"
                role="group"
                aria-label="Filter jobs"
              >
                {(
                  [
                    ["all", "All jobs"],
                    ["backups", "Backups"],
                    ["recoveries", "Recoveries"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    aria-pressed={jobsFilter === value}
                    className={jobsFilter === value ? "is-active" : ""}
                    key={value}
                    onClick={() => setJobsFilter(value)}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}

            {isLifecycleEntry ? (
              <section
                aria-label="Trello authorization controls"
                className="lifecycle-panel"
              >
                <div className="lifecycle-status">
                  <div>
                    <strong>Trello browser authorization</strong>
                    <span>
                      {authorizationState === "authorized"
                        ? "Authorized"
                        : authorizationState === "checking"
                          ? "Checking…"
                          : "Not authorized"}
                    </span>
                  </div>
                  <div>
                    <strong>Managed Cloud connection</strong>
                    <span>
                      {cloudState
                        ? `Connected for ${cloudState.workspace.name}`
                        : "Not checked in this modal"}
                    </span>
                  </div>
                </div>
                <p className="lifecycle-explanation">
                  Reconnect requests fresh Trello read/write consent for backup
                  and restore. Removing the browser token only clears this
                  browser; it does not stop schedules, revoke the encrypted
                  server credential, or delete retained backups.
                </p>
                <div className="lifecycle-actions">
                  <button
                    disabled={
                      authorizationState === "authorizing" ||
                      authorizationState === "connecting" ||
                      authorizationState === "checking"
                    }
                    onClick={() => {
                      void authorizeTrello("cloud");
                    }}
                    type="button"
                  >
                    {authorizationState === "authorizing"
                      ? "Waiting for Trello…"
                      : authorizationState === "connecting"
                        ? "Connecting…"
                        : authorizationState === "authorized"
                          ? "Reconnect Trello access"
                          : "Authorize Trello access"}
                  </button>
                  <button
                    className="secondary-action"
                    disabled={authorizationState !== "authorized"}
                    onClick={() => {
                      void clearBrowserAuthorization();
                    }}
                    type="button"
                  >
                    Remove browser token
                  </button>
                </div>
                {lifecycleMessage || authorizationError ? (
                  <p
                    className={
                      authorizationError
                        ? "lifecycle-result lifecycle-result-error"
                        : "lifecycle-result"
                    }
                    role="status"
                  >
                    {authorizationError ?? lifecycleMessage}
                  </p>
                ) : null}
                {lifecycleEntry === "settings" ? (
                  <div className="lifecycle-disconnect">
                    <strong>Disconnect managed Cloud Preserve</strong>
                    <p>
                      This stops schedules and new managed work, revokes stored
                      Trello access and active download sessions, and removes
                      this browser’s token when possible. Existing backups are
                      not deleted; they remain until their current expiry dates.
                      This is not account deletion.
                    </p>
                    <label>
                      <input
                        checked={disconnectConfirmed}
                        disabled={cloudDisconnectState === "disconnecting"}
                        onChange={(event) => {
                          setDisconnectConfirmed(event.target.checked);
                        }}
                        type="checkbox"
                      />
                      I understand that managed backups and schedules will stop.
                    </label>
                    <button
                      className="disconnect-action"
                      disabled={
                        !disconnectConfirmed ||
                        cloudDisconnectState === "disconnecting"
                      }
                      onClick={() => {
                        void disconnectManagedCloudPreserve();
                      }}
                      type="button"
                    >
                      {cloudDisconnectState === "disconnecting"
                        ? "Disconnecting…"
                        : "Disconnect Cloud Preserve"}
                    </button>
                  </div>
                ) : (
                  <p className="lifecycle-boundary">
                    Managed disconnect controls are available from Power-Up
                    settings. Disconnecting is separate from account deletion.
                  </p>
                )}
              </section>
            ) : null}

            {cloudState && cloudTab === "recover" && recoveryIntent === null ? (
              <section
                className="recovery-task-hub"
                aria-labelledby="cloud-title"
              >
                <button
                  className="recovery-route recovery-route-primary"
                  onClick={() => {
                    selectCloudTab("history");
                  }}
                  type="button"
                >
                  <strong>Browse cloud backups</strong>
                  <span>
                    Download an archive or choose a retained recovery point.
                  </span>
                </button>
                <button
                  className="recovery-route"
                  onClick={() => {
                    setRecoveryIntent("zip");
                  }}
                  type="button"
                >
                  <strong>Restore from a ZIP</strong>
                  <span>
                    Upload a Board Preserve ZIP, including one exported locally.
                  </span>
                </button>
                {cloudState.entitlements.restore ? (
                  <>
                    <button
                      className="recovery-route"
                      onClick={() => {
                        setRecoveryIntent("new_board");
                      }}
                      type="button"
                    >
                      <strong>Create a recovery board</strong>
                      <span>
                        Safest option. Build a new private board without
                        changing this one.
                      </span>
                    </button>
                    <button
                      className="recovery-route"
                      onClick={() => {
                        setRecoveryIntent("selective");
                      }}
                      type="button"
                    >
                      <strong>Recover one card or list</strong>
                      <span>
                        Recreate one missing item on this board without
                        overwriting anything.
                      </span>
                    </button>
                    <button
                      className="recovery-route recovery-route-danger"
                      onClick={() => {
                        setRecoveryIntent("in_place");
                      }}
                      type="button"
                    >
                      <span className="recovery-route-title">
                        <strong>Restore onto this board</strong>
                        <span className="advanced-chip">Advanced</span>
                      </span>
                      <span>
                        Changes the live board. Comparison, a safety backup, and
                        typed confirmation are required.
                      </span>
                    </button>
                  </>
                ) : (
                  <section className="entitlement-gate">
                    <div>
                      <span className="feature-badge">Pro</span>
                      <strong>Managed cloud recovery</strong>
                    </div>
                    <p>
                      Trial includes one recovery board from an uploaded ZIP.
                      Pro adds cloud-backup recovery, single-item recovery,
                      comparison, and guarded recovery onto this board.
                    </p>
                    <button
                      className="secondary-action"
                      onClick={() => {
                        selectCloudTab("plan");
                      }}
                      type="button"
                    >
                      Compare plans
                    </button>
                  </section>
                )}
                {(cloudRestoreHistory?.restores.length ?? 0) > 0 ? (
                  <button
                    className="recovery-route"
                    onClick={() => {
                      setRecoveryIntent(null);
                      setJobsFilter("recoveries");
                      selectCloudTab("history");
                    }}
                    type="button"
                  >
                    <strong>Recovery jobs</strong>
                    <span>
                      Follow active recovery work and review recent outcomes.
                    </span>
                  </button>
                ) : null}
              </section>
            ) : null}

            {cloudState && cloudTab === "backup" ? (
              <ManagedBackupPanel
                captureMessage={cloudCaptureMessage}
                capturePercent={cloudCapturePercent ?? 0}
                captureProgress={cloudCaptureProgress}
                capturePurpose={cloudCapturePurpose}
                captureState={cloudCaptureState}
                captureTiming={cloudCaptureTiming}
                cloudState={cloudState}
                controlBusy={
                  cloudCaptureJobId !== null &&
                  (cloudJobControl?.jobId === cloudCaptureJobId ||
                    cloudJobPendingActions[cloudCaptureJobId] !== undefined)
                }
                observedAt={cloudCaptureProgressObservedAt}
                onControl={(action) => {
                  if (!cloudCaptureJobId) return;
                  void controlActiveCloudJob(
                    cloudCaptureJobId,
                    action,
                    "capture",
                  );
                }}
                onHistory={() => {
                  selectCloudTab("history");
                }}
                onPlan={() => {
                  setPlanView("compare");
                  selectCloudTab("plan");
                }}
                onStart={() => {
                  void runCloudCapture();
                }}
                pendingAction={
                  cloudCaptureJobId
                    ? cloudJobPendingActions[cloudCaptureJobId]
                    : undefined
                }
                trialAvailability={cloudTrialAvailability}
              />
            ) : null}

            {cloudState && cloudDraft && cloudTab === "schedule" ? (
              <BackupSchedulePanel
                browserTimeZone={browserTimeZone}
                cloudState={cloudState}
                dirty={cloudSettingsDirty}
                draft={cloudDraft}
                error={cloudSettingsError}
                onCancel={() => {
                  setCloudDraft({
                    retentionDays: cloudState.settings.retentionDays,
                    retentionTestMinutes:
                      cloudState.settings.retentionTestMinutes ?? null,
                    schedule: cloudState.settings.schedule,
                    scheduleTestMinutes:
                      cloudState.settings.scheduleTestMinutes ?? null,
                  });
                  setCloudSaveState("idle");
                  setCloudSettingsError(null);
                  setSelectedMode(null);
                }}
                onChange={(draft) => {
                  setCloudDraft(draft);
                  setCloudSaveState("idle");
                  setCloudSettingsError(null);
                }}
                onComparePlans={() => {
                  setPlanView("compare");
                  selectCloudTab("plan");
                }}
                onReconnect={() => {
                  void authorizeTrello("cloud");
                }}
                onSave={() => {
                  void saveCloudSettings().then((saved) => {
                    if (saved) setSelectedMode(null);
                  });
                }}
                saveState={cloudSaveState}
                testOptionsEnabled={
                  import.meta.env.VITE_RETENTION_TEST_ENABLED === "true"
                }
                timeZones={scheduleTimeZones}
              />
            ) : null}

            {cloudState ? (
              <section
                aria-labelledby="cloud-title"
                className="cloud-history"
                hidden={
                  cloudTab !== "history" &&
                  !(cloudTab === "recover" && recoveryIntent !== null)
                }
                id="cloud-history-panel"
              >
                {cloudTab === "history" && jobsFilter !== "recoveries" ? (
                  <section
                    className="jobs-section"
                    aria-labelledby="backup-jobs-title"
                  >
                    {jobsFilter === "all" ? (
                      <header className="jobs-section-heading">
                        <h3 id="backup-jobs-title">Backups</h3>
                        <p>
                          Saved recovery points and recent capture attempts.
                        </p>
                      </header>
                    ) : null}
                    <BackupHistoryPanel
                      canCompare={cloudState.entitlements.compare}
                      canRestore={cloudState.entitlements.restore}
                      downloadMessage={cloudDownloadMessage}
                      downloadingSnapshotId={
                        cloudDownloadState === "downloading"
                          ? cloudDownloadSnapshotId
                          : null
                      }
                      deleteError={cloudDeleteError}
                      deletingSnapshotId={cloudDeleteSnapshotId}
                      error={cloudHistoryError}
                      failedCapture={
                        cloudCaptureState === "failed" && cloudLastFailedCapture
                          ? {
                              message: cloudCaptureMessage,
                              status: cloudLastFailedCapture,
                            }
                          : cloudHistory?.failedCaptures[0]
                            ? {
                                message: null,
                                status: cloudHistory.failedCaptures[0],
                              }
                            : null
                      }
                      loading={cloudHistoryState === "loading"}
                      onCompare={(snapshotId) => {
                        selectRestoreSnapshot(snapshotId);
                        setRecoveryIntent("compare");
                        selectCloudTab("recover");
                      }}
                      onCreateRecoveryBoard={(snapshotId) => {
                        selectRestoreSnapshot(snapshotId);
                        setRecoveryIntent("new_board");
                        selectCloudTab("recover");
                      }}
                      onDelete={(snapshot, confirmation) => {
                        void deleteSnapshotFromHistory(
                          snapshot.id,
                          confirmation,
                        );
                      }}
                      onDownload={(snapshot) => {
                        void downloadSnapshot(
                          snapshot.id,
                          snapshot.capturedAt,
                          snapshot.totalBytes,
                        );
                      }}
                      onOpenPlans={() => {
                        setPlanView("compare");
                        selectCloudTab("plan");
                      }}
                      onRecoverItem={(snapshotId) => {
                        selectRestoreSnapshot(snapshotId);
                        setRecoveryIntent("selective");
                        selectCloudTab("recover");
                      }}
                      onRestoreOriginal={(snapshotId) => {
                        selectRestoreSnapshot(snapshotId);
                        setRecoveryIntent("in_place");
                        selectCloudTab("recover");
                      }}
                      onRetryFailedCapture={() => {
                        selectCloudTab("backup");
                        void runCloudCapture();
                      }}
                      onRetryHistory={() => {
                        void loadCloudSnapshots();
                      }}
                      onSelect={(snapshotId) => {
                        setUploadedRestoreSnapshotId(null);
                        selectRestoreSnapshot(snapshotId);
                      }}
                      onWarningLoad={(snapshotId) => {
                        void loadSnapshotWarnings(snapshotId);
                      }}
                      selectedSnapshotId={selectedRestoreSnapshotId}
                      snapshots={cloudHistory?.snapshots ?? []}
                      warningReports={cloudWarningReports}
                    />
                  </section>
                ) : null}
                {trelloSetup.client &&
                cloudTab === "recover" &&
                recoveryIntent === "zip" ? (
                  <RestoreFromZipGate
                    access={
                      cloudState.entitlements.restore
                        ? "pro"
                        : cloudState.zipRestoreTrial?.status === "used"
                          ? "trial_used"
                          : "trial_available"
                    }
                    consumedAt={
                      cloudState.zipRestoreTrial?.status === "used"
                        ? cloudState.zipRestoreTrial.consumedAt
                        : null
                    }
                  >
                    <UploadedRestorePanel
                      apiUrl={readCloudApiUrl(
                        import.meta.env.VITE_BOARD_PRESERVE_API_URL,
                      )}
                      boardName={cloudState.board.name}
                      client={trelloSetup.client}
                      clearDisabled={
                        cloudRestoreConfirmState === "submitting" ||
                        cloudRestoreConfirmState === "submitted"
                      }
                      onClear={() => {
                        setUploadedRestoreSnapshotId(null);
                        selectRestoreSnapshot(
                          resolveRestoreSnapshotSelection(
                            cloudHistory?.snapshots.map(({ id }) => id) ?? [],
                            null,
                          ),
                        );
                      }}
                      onContinue={() => {
                        setRecoveryIntent("new_board");
                        void previewSelectedRestore();
                      }}
                      onReady={(snapshotId) => {
                        selectRestoreSnapshot(snapshotId);
                        setUploadedRestoreSnapshotId(snapshotId);
                      }}
                      planLabel={cloudPlanLabel(
                        cloudState.entitlements.effectivePlan,
                      )}
                      workspaceName={cloudState.workspace.name}
                    />
                  </RestoreFromZipGate>
                ) : null}
                {cloudTab === "recover" &&
                recoveryIntent !== "zip" &&
                !cloudState.entitlements.restore ? (
                  <section
                    className="entitlement-gate"
                    aria-label="Paid recovery features"
                  >
                    <div>
                      <span className="feature-badge">Pro</span>
                      <strong>Recover from a stored cloud backup</strong>
                    </div>
                    <p>
                      Your trial backup stays downloadable. Pro unlocks a new
                      recovery board, one-card or one-list recovery, backup
                      comparison, and guarded recovery onto the original board.
                    </p>
                    <button
                      className="secondary-action"
                      onClick={() => {
                        selectCloudTab("plan");
                      }}
                      type="button"
                    >
                      Compare plans
                    </button>
                  </section>
                ) : null}
                {((cloudTab === "recover" && recoveryIntent === "reports") ||
                  (cloudTab === "history" && jobsFilter !== "backups")) &&
                cloudRestoreHistoryState === "loading" ? (
                  <p className="history-status">Checking restore reports…</p>
                ) : null}
                {((cloudTab === "recover" && recoveryIntent === "reports") ||
                  (cloudTab === "history" && jobsFilter !== "backups")) &&
                cloudRestoreHistoryError ? (
                  <p className="history-error">{cloudRestoreHistoryError}</p>
                ) : null}
                {((cloudTab === "recover" && recoveryIntent === "reports") ||
                  (cloudTab === "history" && jobsFilter !== "backups")) &&
                (cloudRestoreHistory?.restores.length ?? 0) > 0 ? (
                  <div className="restore-history" aria-label="Restore reports">
                    <div className="restore-history-heading">
                      <div>
                        <strong>Restore reports</strong>
                        <span>
                          Newest first · reports are kept for 30 days.
                        </span>
                      </div>
                      {sortedRestoreReports.length > 4 ? (
                        <button
                          aria-expanded={restoreReportsExpanded}
                          onClick={() => {
                            setRestoreReportsExpanded((current) => !current);
                          }}
                          type="button"
                        >
                          {restoreReportsExpanded
                            ? "Show newest 4"
                            : `Show all ${sortedRestoreReports.length}`}
                        </button>
                      ) : null}
                    </div>
                    {visibleRestoreReports.map((restore) => (
                      <article
                        className={`restore-row${cloudRestoreReportJobId === restore.jobId ? " is-open" : ""}`}
                        key={restore.jobId}
                      >
                        <div className="restore-row-summary">
                          <div>
                            <strong>
                              {restore.controlState === "pause_requested"
                                ? "Pausing"
                                : restore.controlState === "cancel_requested"
                                  ? "Cancelling"
                                  : formatRestoreStatus(restore.status)}
                            </strong>
                            <span>
                              Started{" "}
                              {new Date(restore.createdAt).toLocaleString()}
                            </span>
                            <span>
                              {restore.source === "uploaded_zip"
                                ? "Uploaded ZIP"
                                : "Cloud Preserve backup"}{" "}
                              · job {restore.jobId.slice(0, 8)}
                            </span>
                            <span>
                              {restore.progress.completed} of{" "}
                              {restore.progress.total} operations ·{" "}
                              {restore.outcomes.created} created ·{" "}
                              {restore.outcomes.skipped} skipped ·{" "}
                              {restore.outcomes.failed} failed
                            </span>
                            <div
                              className={
                                restoreProgressPresentation(
                                  restore.status,
                                  progressPercentage(
                                    restore.progress.completed,
                                    restore.progress.total,
                                  ),
                                ).className
                              }
                            >
                              <div className="job-progress-label">
                                <span>Restore progress</span>
                                <strong>
                                  {progressPercentage(
                                    restore.progress.completed,
                                    restore.progress.total,
                                  )}
                                  %
                                </strong>
                              </div>
                              <progress
                                aria-label={
                                  restoreProgressPresentation(
                                    restore.status,
                                    progressPercentage(
                                      restore.progress.completed,
                                      restore.progress.total,
                                    ),
                                  ).ariaLabel
                                }
                                max={Math.max(restore.progress.total, 1)}
                                value={restore.progress.completed}
                              />
                              <span className="job-progress-eta">
                                {progressTimeRemainingPresentation({
                                  completed: restore.progress.completed,
                                  controlState: restore.controlState,
                                  startedAt: restore.startedAt,
                                  status: restore.status,
                                  total: restore.progress.total,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="restore-row-actions">
                            <button
                              aria-expanded={
                                cloudRestoreReportJobId === restore.jobId
                              }
                              disabled={
                                cloudRestoreReportState === "loading" &&
                                cloudRestoreReportJobId === restore.jobId
                              }
                              onClick={() => {
                                const nextJobId = nextRestoreReportJobId(
                                  cloudRestoreReportJobId,
                                  restore.jobId,
                                );
                                if (nextJobId === null) {
                                  setCloudRestoreReportJobId(null);
                                  setCloudRestoreReport(null);
                                  setCloudRestoreReportError(null);
                                  setCloudRestoreReportState("idle");
                                  return;
                                }
                                void loadCloudRestoreReport(nextJobId);
                              }}
                              type="button"
                            >
                              {restoreReportDisclosureLabel(
                                cloudRestoreReportJobId === restore.jobId,
                                cloudRestoreReportState === "loading" &&
                                  cloudRestoreReportJobId === restore.jobId,
                              )}
                            </button>
                            {restore.status === "pending" ||
                            restore.status === "running" ||
                            restore.status === "paused" ? (
                              <div
                                className="job-control-actions"
                                aria-label={`Controls for restore started ${new Date(
                                  restore.createdAt,
                                ).toLocaleString()}`}
                              >
                                <button
                                  disabled={
                                    cloudJobControl?.jobId === restore.jobId ||
                                    cloudJobPendingActions[restore.jobId] !==
                                      undefined
                                  }
                                  onClick={() => {
                                    void controlActiveCloudJob(
                                      restore.jobId,
                                      restore.status === "paused"
                                        ? "resume"
                                        : "pause",
                                      "restore",
                                    );
                                  }}
                                  type="button"
                                >
                                  {cloudJobPendingActions[restore.jobId] ===
                                  "pause"
                                    ? "Pausing…"
                                    : cloudJobControl?.jobId ===
                                          restore.jobId &&
                                        cloudJobControl.action !== "cancel"
                                      ? restore.status === "paused"
                                        ? "Resuming…"
                                        : "Pausing…"
                                      : restore.status === "paused"
                                        ? "Resume"
                                        : "Pause"}
                                </button>
                                <button
                                  className="danger"
                                  disabled={
                                    cloudJobControl?.jobId === restore.jobId ||
                                    cloudJobPendingActions[restore.jobId] !==
                                      undefined
                                  }
                                  onClick={() => {
                                    void controlActiveCloudJob(
                                      restore.jobId,
                                      "cancel",
                                      "restore",
                                    );
                                  }}
                                  type="button"
                                >
                                  {cloudJobPendingActions[restore.jobId] ===
                                  "cancel"
                                    ? "Cancelling…"
                                    : cloudJobControl?.jobId ===
                                          restore.jobId &&
                                        cloudJobControl.action === "cancel"
                                      ? "Cancelling…"
                                      : "Cancel"}
                                </button>
                              </div>
                            ) : null}
                            {(restore.status === "failed" ||
                              (restore.status === "complete_with_warnings" &&
                                restore.outcomes.failed > 0)) &&
                            restore.controlState !== "canceled" ? (
                              <button
                                disabled={
                                  cloudJobControl?.jobId === restore.jobId
                                }
                                onClick={() => {
                                  void controlActiveCloudJob(
                                    restore.jobId,
                                    "retry",
                                    "restore",
                                  );
                                }}
                                type="button"
                              >
                                {cloudJobControl?.jobId === restore.jobId &&
                                cloudJobControl.action === "retry"
                                  ? "Retrying…"
                                  : "Retry failed items"}
                              </button>
                            ) : null}
                            {restore.source === "uploaded_zip" &&
                            (restore.status === "failed" ||
                              (restore.status === "complete_with_warnings" &&
                                restore.outcomes.failed > 0)) ? (
                              <button
                                onClick={() => {
                                  setRestoreReuploadJobId((current) =>
                                    current === restore.jobId
                                      ? null
                                      : restore.jobId,
                                  );
                                }}
                                type="button"
                              >
                                {restoreReuploadJobId === restore.jobId
                                  ? "Hide ZIP re-upload"
                                  : "Re-upload source ZIP"}
                              </button>
                            ) : null}
                          </div>
                        </div>
                        {restoreReuploadJobId === restore.jobId &&
                        trelloSetup.client ? (
                          <UploadedRestorePanel
                            apiUrl={readCloudApiUrl(
                              import.meta.env.VITE_BOARD_PRESERVE_API_URL,
                            )}
                            boardName={cloudState.board.name}
                            client={trelloSetup.client}
                            onClear={() => {
                              setRestoreReuploadJobId(null);
                            }}
                            onReady={(_snapshotId, importId) => {
                              void attachReplacementZipAndRetry(
                                restore.jobId,
                                importId,
                              );
                            }}
                            planLabel={cloudPlanLabel(
                              cloudState.entitlements.effectivePlan,
                            )}
                            purpose="retry_restore"
                            workspaceName={cloudState.workspace.name}
                          />
                        ) : null}
                        {restore.status === "pending" ||
                        restore.status === "running" ||
                        restore.status === "paused" ? (
                          <p className="job-control-note">
                            Pause waits for a safe checkpoint. Cancel stops only
                            remaining operations; Trello items already created
                            by this restore remain.
                          </p>
                        ) : null}
                        {cloudJobControlMessages[restore.jobId] ? (
                          <p className="job-control-status" role="status">
                            {cloudJobControlMessages[restore.jobId]}
                          </p>
                        ) : null}
                        {cloudRestoreReportError &&
                        cloudRestoreReportJobId === restore.jobId ? (
                          <p className="history-error">
                            {cloudRestoreReportError}
                          </p>
                        ) : null}
                        {cloudRestoreReport?.jobId === restore.jobId ? (
                          <div className="restore-operation-report">
                            <div className="restore-report-actions">
                              <span>
                                Showing the latest recorded object outcomes.
                              </span>
                              <button
                                disabled={cloudRestoreReportState === "loading"}
                                onClick={() => {
                                  void loadCloudRestores();
                                  void loadCloudRestoreReport(restore.jobId);
                                }}
                                type="button"
                              >
                                {cloudRestoreReportState === "loading"
                                  ? "Refreshing…"
                                  : "Refresh report"}
                              </button>
                            </div>
                            <ol>
                              {cloudRestoreReport.operations.map(
                                (operation) => (
                                  <li key={operation.sequence}>
                                    <span className="operation-outcome">
                                      <b>#{operation.sequence}</b>{" "}
                                      {operation.operationType.replaceAll(
                                        "_",
                                        " ",
                                      )}
                                      {" · "}
                                      {operation.status}
                                      {operation.resultCode
                                        ? ` · ${formatRestoreResultCode(operation.resultCode)}`
                                        : ""}
                                    </span>
                                    <code>
                                      source {operation.sourceId}
                                      {operation.destinationId
                                        ? ` → ${operation.destinationId}`
                                        : ""}
                                    </code>
                                  </li>
                                ),
                              )}
                            </ol>
                            {cloudRestoreReport.nextAfter !== null ? (
                              <button
                                disabled={cloudRestoreReportState === "loading"}
                                onClick={() => {
                                  void loadCloudRestoreReport(
                                    restore.jobId,
                                    cloudRestoreReport.nextAfter ?? 0,
                                  );
                                }}
                                type="button"
                              >
                                {cloudRestoreReportState === "loading"
                                  ? "Loading…"
                                  : "Load more outcomes"}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : null}
                {cloudTab === "history" &&
                jobsFilter !== "backups" &&
                cloudRestoreHistoryState !== "loading" &&
                !cloudRestoreHistoryError &&
                (cloudRestoreHistory?.restores.length ?? 0) === 0 ? (
                  <section className="jobs-empty">
                    <strong>No recovery jobs yet</strong>
                    <p>
                      Recovery work will appear here after you create a recovery
                      board or recover a card or list.
                    </p>
                  </section>
                ) : null}
                {cloudTab === "recover" &&
                recoveryIntent === "compare" &&
                cloudState.entitlements.compare &&
                trelloSetup.client &&
                cloudHistory &&
                cloudHistory.snapshots.length >= 2 ? (
                  <section className="recovery-action-panel recovery-action-panel-compare">
                    <SnapshotComparisonPanel
                      apiUrl={readCloudApiUrl(
                        import.meta.env.VITE_BOARD_PRESERVE_API_URL,
                      )}
                      boardName={cloudState.board.name}
                      client={trelloSetup.client}
                      snapshots={cloudHistory.snapshots}
                    />
                  </section>
                ) : null}
                {cloudTab === "recover" &&
                recoveryIntent === "compare" &&
                cloudState.entitlements.compare &&
                cloudHistory &&
                cloudHistory.snapshots.length < 2 ? (
                  <section className="backup-compare-empty">
                    <strong>A second backup is needed</strong>
                    <p>
                      Backup-to-backup comparison needs two saved versions. You
                      can still compare this backup with the live board through
                      the guarded original-board recovery flow.
                    </p>
                    <button
                      onClick={() => setRecoveryIntent("in_place")}
                      type="button"
                    >
                      Compare with the live board
                    </button>
                  </section>
                ) : null}
                {cloudTab === "recover" &&
                (recoveryIntent === "new_board" ||
                  recoveryIntent === "selective" ||
                  recoveryIntent === "in_place") &&
                (cloudState.entitlements.restore ||
                  (cloudState.zipRestoreTrial?.status !== "used" &&
                    uploadedRestoreSnapshotId !== null)) ? (
                  <div className="recovery-workflow">
                    <RecoveryPointPicker
                      onSelect={(snapshotId) => {
                        selectRestoreSnapshot(snapshotId);
                      }}
                      selectedSnapshotId={selectedRestoreSnapshotId}
                      snapshots={cloudHistory?.snapshots ?? []}
                      uploaded={
                        uploadedRestoreSnapshotId
                          ? {
                              capturedAt:
                                cloudRestorePreview?.snapshot.capturedAt ??
                                null,
                              id: uploadedRestoreSnapshotId,
                            }
                          : null
                      }
                    />
                    {recoveryIntent === "new_board" ? (
                      <section className="recovery-action-panel recovery-action-panel-safe">
                        <div className="restore-preview-callout">
                          <div className="restore-preview-heading">
                            <div>
                              <strong>Preview the recovery board</strong>
                              <p>
                                See exactly what will be created. This preview
                                makes no changes in Trello.
                              </p>
                            </div>
                            <button
                              disabled={
                                selectedRestoreSnapshotId === null ||
                                cloudRestorePreviewState === "loading"
                              }
                              onClick={() => {
                                void previewSelectedRestore();
                              }}
                              type="button"
                            >
                              {cloudRestorePreviewState === "loading"
                                ? "Preparing…"
                                : "Preview recovery board"}
                            </button>
                          </div>
                          {cloudRestorePreviewError ? (
                            <p className="restore-preview-error">
                              {cloudRestorePreviewError}
                            </p>
                          ) : null}
                          {cloudRestorePreview ? (
                            <div
                              className="restore-preview-result"
                              aria-live="polite"
                            >
                              <p className="restore-preview-total">
                                <strong>
                                  {cloudRestorePreview.totals.planned}
                                </strong>
                                <span>
                                  planned operations · 0 Trello writes
                                </span>
                              </p>
                              <ul>
                                {cloudRestorePreview.steps
                                  .filter(({ count }) => count > 0)
                                  .slice(0, 7)
                                  .map((step) => (
                                    <li key={step.type}>
                                      <span>{step.count}</span>{" "}
                                      {step.type.replaceAll("_", " ")}
                                      {step.fidelity === "best_effort"
                                        ? " · best effort"
                                        : ""}
                                    </li>
                                  ))}
                              </ul>
                              <div className="restore-fidelity-summary">
                                <strong>Restore limits</strong>
                                <ul>
                                  <li>
                                    Restored items are created by the connected
                                    administrator now.
                                  </li>
                                  <li>
                                    Comments and activity become labelled notes
                                    with the original author and date.
                                  </li>
                                  <li>
                                    Some provider photos and board backgrounds
                                    cannot be recreated.
                                  </li>
                                </ul>
                              </div>
                              <details className="restore-fidelity-details">
                                <summary>
                                  Review {cloudRestorePreview.warnings.length}{" "}
                                  exact restore limit
                                  {cloudRestorePreview.warnings.length === 1
                                    ? ""
                                    : "s"}
                                </summary>
                                <ul>
                                  {cloudRestorePreview.warnings.map(
                                    (warning) => (
                                      <li key={warning.code}>
                                        <span>{warning.count}</span>{" "}
                                        {warning.message}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </details>
                              <div className="restore-confirmation-checks">
                                {cloudRestorePreview.duplicateRestore
                                  .requiresExplicitOverride ? (
                                  <div
                                    className="restore-duplicate-warning"
                                    role="alert"
                                  >
                                    <strong>
                                      Recovery board already created
                                    </strong>
                                    <p>
                                      {
                                        cloudRestorePreview.duplicateRestore
                                          .priorRestoreCount
                                      }{" "}
                                      prior restore
                                      {cloudRestorePreview.duplicateRestore
                                        .priorRestoreCount === 1
                                        ? ""
                                        : "s"}{" "}
                                      from this backup already created a
                                      recovery board in this Workspace.
                                      Continuing creates another separate
                                      private board; it does not update or
                                      replace an existing recovery board.
                                    </p>
                                    <label>
                                      <input
                                        checked={
                                          cloudRestoreAcknowledgements.duplicateRestore
                                        }
                                        onChange={(event) => {
                                          setCloudRestoreAcknowledgements(
                                            (current) => ({
                                              ...current,
                                              duplicateRestore:
                                                event.target.checked,
                                            }),
                                          );
                                        }}
                                        type="checkbox"
                                      />
                                      I intentionally want another recovery
                                      board from this same backup.
                                    </label>
                                  </div>
                                ) : null}
                                <label>
                                  <input
                                    checked={
                                      cloudRestoreAcknowledgements.newBoard
                                    }
                                    onChange={(event) => {
                                      setCloudRestoreAcknowledgements(
                                        (current) => ({
                                          ...current,
                                          newBoard: event.target.checked,
                                        }),
                                      );
                                    }}
                                    type="checkbox"
                                  />
                                  Create a new private recovery board in this
                                  Workspace.
                                </label>
                                <label>
                                  <input
                                    checked={
                                      cloudRestoreAcknowledgements.noMutation
                                    }
                                    onChange={(event) => {
                                      setCloudRestoreAcknowledgements(
                                        (current) => ({
                                          ...current,
                                          noMutation: event.target.checked,
                                        }),
                                      );
                                    }}
                                    type="checkbox"
                                  />
                                  Do not overwrite, delete, or change the source
                                  board.
                                </label>
                                <label>
                                  <input
                                    checked={
                                      cloudRestoreAcknowledgements.fidelity
                                    }
                                    onChange={(event) => {
                                      setCloudRestoreAcknowledgements(
                                        (current) => ({
                                          ...current,
                                          fidelity: event.target.checked,
                                        }),
                                      );
                                    }}
                                    type="checkbox"
                                  />
                                  I understand that Trello creates new recovery
                                  activity under my account; historical notes
                                  identify their original actor and date but
                                  cannot recreate Trello’s original audit trail.
                                  I also reviewed the cover limitations above.
                                </label>
                                <button
                                  disabled={
                                    cloudRestoreConfirmState === "submitting" ||
                                    cloudRestoreConfirmState === "submitted" ||
                                    !cloudRestoreAcknowledgements.fidelity ||
                                    !cloudRestoreAcknowledgements.newBoard ||
                                    !cloudRestoreAcknowledgements.noMutation ||
                                    (cloudRestorePreview.duplicateRestore
                                      .requiresExplicitOverride &&
                                      !cloudRestoreAcknowledgements.duplicateRestore)
                                  }
                                  onClick={() => {
                                    void confirmSelectedRestore();
                                  }}
                                  type="button"
                                >
                                  {cloudRestoreConfirmState === "submitting"
                                    ? "Confirming…"
                                    : cloudRestoreConfirmState === "submitted"
                                      ? "Restore submitted"
                                      : cloudRestorePreview.duplicateRestore
                                            .requiresExplicitOverride
                                        ? "Create another recovery board"
                                        : "Create recovery board"}
                                </button>
                              </div>
                              {cloudRestoreConfirmError ? (
                                <p className="restore-preview-error">
                                  {cloudRestoreConfirmError}
                                </p>
                              ) : null}
                              {cloudRestorePreparation ? (
                                <p
                                  className="restore-confirmation-status"
                                  role="status"
                                >
                                  {cloudRestorePreparation.dispatchStatus ===
                                  "queued"
                                    ? "Restore queued. Follow its object-by-object progress in Restore reports."
                                    : "Restore plan prepared. Execution remains disabled in this environment."}
                                </p>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </section>
                    ) : null}
                    {recoveryIntent === "selective" &&
                    trelloSetup.client &&
                    selectedRestoreSnapshotId &&
                    selectedRestoreSnapshotId !== uploadedRestoreSnapshotId ? (
                      <section className="recovery-action-panel">
                        <SelectiveRestorePanel
                          apiUrl={readCloudApiUrl(
                            import.meta.env.VITE_BOARD_PRESERVE_API_URL,
                          )}
                          boardName={cloudState.board.name}
                          client={trelloSetup.client}
                          key={selectedRestoreSnapshotId}
                          snapshotId={selectedRestoreSnapshotId}
                        />
                      </section>
                    ) : null}
                    {recoveryIntent === "selective" &&
                    selectedRestoreSnapshotId === uploadedRestoreSnapshotId ? (
                      <section className="recovery-route-notice">
                        <strong>
                          Choose a cloud backup for one-item recovery
                        </strong>
                        <p>
                          Uploaded ZIPs can create a complete recovery board.
                          One-card and one-list recovery uses a retained cloud
                          backup so Board Preserve can verify the item against
                          this exact live board.
                        </p>
                      </section>
                    ) : null}
                    {recoveryIntent === "in_place" &&
                    trelloSetup.client &&
                    cloudState.entitlements.restore ? (
                      <InPlaceRestorePanel
                        apiUrl={readCloudApiUrl(
                          import.meta.env.VITE_BOARD_PRESERVE_API_URL,
                        )}
                        boardName={cloudState.board.name}
                        client={trelloSetup.client}
                        executionEnabled={
                          import.meta.env
                            .VITE_IN_PLACE_RESTORE_EXECUTION_ENABLED === "true"
                        }
                        onSnapshotRequired={(snapshotId) => {
                          if (
                            cloudHistory?.snapshots.some(
                              ({ id }) => id === snapshotId,
                            )
                          ) {
                            setSelectedRestoreSnapshotId(snapshotId);
                          }
                        }}
                        snapshot={selectedInPlaceRestoreSource}
                      />
                    ) : null}
                  </div>
                ) : null}
              </section>
            ) : null}

            {cloudState && cloudTab === "plan" && planView === "usage" ? (
              <PlanAndUsagePanel
                boardName={cloudState.board.name}
                entitlements={cloudState.entitlements}
                onComparePlans={() => {
                  setPlanView("compare");
                }}
                onOpenBackup={() => {
                  selectCloudTab("backup");
                }}
                settings={cloudState.settings}
                trial={cloudState.trial}
                usage={cloudState.usage}
                workspaceName={cloudState.workspace.name}
                zipRestoreTrial={cloudState.zipRestoreTrial}
              />
            ) : null}

            {cloudTab === "plan" && (planView === "compare" || !cloudState) ? (
              <PlanComparisonPanel
                currentPlan={cloudState?.entitlements.effectivePlan ?? "free"}
                onStartTrial={() => {
                  selectCloudTab("backup");
                }}
                workspaceName={cloudState?.workspace.name}
              />
            ) : null}

            {cloudState && cloudTab === "settings" ? (
              <section className="task-settings" aria-labelledby="cloud-title">
                <div className="settings-identity">
                  <div>
                    <strong>Cloud Preserve connection</strong>
                    <span>
                      Connected to {cloudState.workspace.name} as a Trello
                      administrator.
                    </span>
                  </div>
                  <button
                    className="secondary-action"
                    disabled={authorizationState === "checking"}
                    onClick={() => {
                      void refreshCloudConnection();
                    }}
                    type="button"
                  >
                    {authorizationState === "checking"
                      ? "Refreshing…"
                      : "Refresh"}
                  </button>
                </div>
                <div className="settings-list">
                  <section>
                    <div>
                      <strong>Reconnect Trello access</strong>
                      <p>
                        Use this only if your permissions changed or a backup
                        can no longer read this board.
                      </p>
                    </div>
                    <button
                      className="secondary-action"
                      disabled={
                        authorizationState === "authorizing" ||
                        authorizationState === "connecting"
                      }
                      onClick={() => {
                        void authorizeTrello("cloud", "overview");
                      }}
                      type="button"
                    >
                      Reconnect
                    </button>
                  </section>
                  <section>
                    <div>
                      <strong>Remove this browser’s Trello token</strong>
                      <p>
                        Clears this browser only. It does not stop schedules or
                        delete retained backups.
                      </p>
                    </div>
                    <button
                      className="secondary-action"
                      disabled={authorizationState !== "authorized"}
                      onClick={() => {
                        void clearBrowserAuthorization();
                      }}
                      type="button"
                    >
                      Remove token
                    </button>
                  </section>
                  <section className="settings-danger-zone">
                    <div>
                      <strong>Disconnect Cloud Preserve</strong>
                      <p>
                        Stops schedules and managed access. Existing backups
                        remain only until their current expiry dates.
                      </p>
                      <label>
                        <input
                          checked={disconnectConfirmed}
                          disabled={cloudDisconnectState === "disconnecting"}
                          onChange={(event) => {
                            setDisconnectConfirmed(event.target.checked);
                          }}
                          type="checkbox"
                        />
                        I understand that automatic and managed backups will
                        stop.
                      </label>
                    </div>
                    <button
                      className="danger"
                      disabled={
                        !disconnectConfirmed ||
                        cloudDisconnectState === "disconnecting"
                      }
                      onClick={() => {
                        void disconnectManagedCloudPreserve();
                      }}
                      type="button"
                    >
                      {cloudDisconnectState === "disconnecting"
                        ? "Disconnecting…"
                        : "Disconnect"}
                    </button>
                  </section>
                </div>
                {lifecycleMessage || authorizationError ? (
                  <p
                    className={
                      authorizationError ? "history-error" : "download-status"
                    }
                    role="status"
                  >
                    {authorizationError ?? lifecycleMessage}
                  </p>
                ) : null}
              </section>
            ) : null}

            {!cloudState && !isLifecycleEntry && cloudTab !== "plan" ? (
              <div className="detail-footer">
                <div>
                  <p className="status-message">
                    {authorizationMessage("cloud")}
                  </p>
                  <p className="custody-note">
                    Cloud Preserve stores encrypted credentials and private
                    board backups in Nodestone-operated Cloudflare services,
                    using private EU R2 storage for backup objects.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={authorizationDisabled("cloud")}
                  onClick={() => {
                    void authorizeTrello("cloud", "overview");
                  }}
                >
                  {authorizationButtonLabel("cloud")}
                </button>
              </div>
            ) : (
              <p className="connected-custody-note">
                Private EU storage · live Trello administrator checks · exact
                partial-capture warnings
              </p>
            )}
          </section>
        ) : null}
      </section>
    </main>
  );
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element was not found.");
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
